import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useRouterCore } from "../../../cores/router";
import { useMeCore } from "../../../cores/me";
import { useEmailMarketingCore } from "../../../cores/emailMarketing";
import { ContactSourceOptions } from "../../../cores/contacts/config";
import {
  emailMarketingApiService,
  ApiImportableContact,
  ApiTemplateDetail,
} from "../../../cores/emailMarketing/services/api";
import Content from "../../layout/Content";
import Layout from "../../layout/Layout";
import {
  CampaignMessage,
} from "./mockData";
import { EmailMktModal } from "./shared";
import { EmailComposer, DEFAULT_EMAIL_HTML } from "./EmailComposer";
import { DEFAULT_RICH_HTML, wrapRichHtmlForEmail } from "./emailHtmlUtils";
import {
  CampaignAudienceLine,
  CampaignMetric,
  CampaignStatusPill,
  EmailPreviewThumb,
  formatCampaignListDate,
  getCampaignSortTimestamp,
} from "./EmailCampaignListCells";
import "./EmailMarketing.scss";

type ListTab =
  | "basic"
  | "personalize"
  | "confirmation"
  | "subscribers"
  | "segments"
  | "suppressions";
type EmailTab = "all" | "drafts" | "scheduled" | "sent";
type ReportView =
  | "general"
  | "campaigns"
  | "deliverability"
  | "activity"
  | "exports";
type CreateWizardStep = "audience" | "sender" | "schedule";

const TAG_COLOR_PRESETS = [
  { textColor: "#1e40af", backgroundColor: "#dbeafe" },
  { textColor: "#15803d", backgroundColor: "#dcfce7" },
  { textColor: "#b45309", backgroundColor: "#fef3c7" },
  { textColor: "#be185d", backgroundColor: "#fce7f3" },
  { textColor: "#6d28d9", backgroundColor: "#ede9fe" },
];
type CreateType =
  | "dragDrop"
  | "smartDesigner"
  | "plainText"
  | "html";

const reportNavSections: { id: ReportView; label: string }[] = [
  { id: "general", label: "app.emailMarketing.reports.nav.general" },
  { id: "campaigns", label: "app.emailMarketing.reports.nav.campaigns" },
  { id: "deliverability", label: "app.emailMarketing.reports.nav.deliverability" },
  { id: "activity", label: "app.emailMarketing.reports.nav.activity" },
  { id: "exports", label: "app.emailMarketing.reports.nav.exports" },
];

const createTypeLabels: Record<CreateType, string> = {
  dragDrop: "app.emailMarketing.emails.create.dragDrop",
  smartDesigner: "app.emailMarketing.emails.create.smartDesigner",
  plainText: "app.emailMarketing.emails.create.plainText",
  html: "app.emailMarketing.emails.create.html",
};

function escapeRegex(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function applyTestPersonalization(html: string, tagsRaw: string) {
  if (!tagsRaw.trim()) {
    return html;
  }
  let result = html;
  tagsRaw
    .split(/[\n,]+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((entry) => {
      const separator = entry.includes("=") ? "=" : ":";
      const [key, ...rest] = entry.split(separator);
      const tagKey = key.trim();
      const tagValue = rest.join(separator).trim();
      if (!tagKey) {
        return;
      }
      const pattern = escapeRegex(tagKey);
      result = result.replace(new RegExp(`{{\\s*${pattern}\\s*}}`, "gi"), tagValue);
      result = result.replace(new RegExp(`%${pattern}%`, "gi"), tagValue);
    });
  return result;
}

function defaultScheduleDateTime() {
  const date = new Date(Date.now() + 3600000);
  date.setMinutes(0, 0, 0);
  if (date.getTime() <= Date.now()) {
    date.setHours(date.getHours() + 1);
  }
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

function mapTemplateTypeToCreateType(type: string): CreateType {
  if (type === "dragDrop") return "dragDrop";
  if (type === "plain") return "plainText";
  if (type === "html") return "html";
  return "dragDrop";
}

function resolveComposerHtmlFromTemplate(type: CreateType, html: string) {
  if (!html?.trim()) {
    return type === "plainText" ? DEFAULT_RICH_HTML : DEFAULT_EMAIL_HTML;
  }
  return html;
}

function sourceLabel(source: string, intl: ReturnType<typeof useIntl>) {
  const option = ContactSourceOptions.find((item) => item.value === source);
  if (option) {
    return intl.formatMessage({ id: option.label });
  }
  return source || intl.formatMessage({ id: "app.contacts.sourceUndefined" });
}

function fieldTypeLabel(type: string) {
  switch (type) {
    case "number":
      return "app.emailMarketing.lists.fieldType.number";
    case "date":
      return "app.emailMarketing.lists.fieldType.date";
    default:
      return "app.emailMarketing.lists.fieldType.text";
  }
}

export const EmailMarketingListsScreen = () => {
  const intl = useIntl();
  const { company } = useMeCore();
  const {
    overview,
    listSetting,
    subscribers: apiSubscribers,
    suppressions: apiSuppressions,
    config,
    customFields,
    listSegments,
    listTags,
    marketingLists,
    defaultListId,
    physicalAddressOptions,
    loadOverview,
    loadList,
    loadSubscribers,
    importSubscribers,
    importSubscribersBulk,
    deleteSubscriber,
    createSubscriber,
    loadSuppressions,
    loadConfig,
    updateList,
    loadCustomFields,
    loadListSegments,
    loadListTags,
    loadMarketingLists,
    createMarketingList,
    loadPhysicalAddressOptions,
    createCustomField,
    createListSegment,
    addNotificationContact,
    removeNotificationContact,
    createListTag,
  } = useEmailMarketingCore();
  const [activeTab, setActiveTab] = useState<ListTab>("basic");
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importTab, setImportTab] = useState<"contacts" | "file" | "paste" | "manual">("contacts");
  const [pasteValue, setPasteValue] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [manualName, setManualName] = useState("");
  const [importing, setImporting] = useState(false);
  const [importContactRows, setImportContactRows] = useState<ApiImportableContact[]>([]);
  const [importContactSearch, setImportContactSearch] = useState("");
  const [importContactSearchDebounced, setImportContactSearchDebounced] = useState("");
  const [importSourceFilters, setImportSourceFilters] = useState<string[]>([]);
  const [selectedImportContactIds, setSelectedImportContactIds] = useState<number[]>([]);
  const [loadingImportContacts, setLoadingImportContacts] = useState(false);
  const [deletingSubscriberEmail, setDeletingSubscriberEmail] = useState<string | null>(null);
  const [fieldModalOpen, setFieldModalOpen] = useState(false);
  const [segmentModalOpen, setSegmentModalOpen] = useState(false);
  const [subscriberSearch, setSubscriberSearch] = useState("");
  const [fieldSearch, setFieldSearch] = useState("");
  const [tagSearch, setTagSearch] = useState("");
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColorIndex, setNewTagColorIndex] = useState(0);
  const [tagImportingId, setTagImportingId] = useState<string | null>(null);
  const [subscriberTagFilter, setSubscriberTagFilter] = useState<string | null>(null);
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldType, setNewFieldType] = useState<"text" | "number" | "date">("text");
  const [newSegmentName, setNewSegmentName] = useState("");
  const [listName, setListName] = useState("");
  const [listDescription, setListDescription] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [selectedAddressLabel, setSelectedAddressLabel] = useState("");
  const [customAddressLine, setCustomAddressLine] = useState("");
  const [notificationEmail, setNotificationEmail] = useState("");
  const [notificationName, setNotificationName] = useState("");
  const [useCustomAddress, setUseCustomAddress] = useState(false);
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [createListModalOpen, setCreateListModalOpen] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [newListDescription, setNewListDescription] = useState("");
  const [creatingList, setCreatingList] = useState(false);

  useEffect(() => {
    if (!company?.id) {
      return;
    }
    loadOverview(company.id);
    loadList(company.id);
    loadMarketingLists(company.id);
    loadSuppressions(company.id);
    loadConfig(company.id);
    loadCustomFields(company.id);
    loadListSegments(company.id);
    loadListTags(company.id);
    loadPhysicalAddressOptions(company.id);
  }, [
    company?.id,
    loadOverview,
    loadList,
    loadMarketingLists,
    loadSuppressions,
    loadConfig,
    loadCustomFields,
    loadListSegments,
    loadListTags,
    loadPhysicalAddressOptions,
  ]);

  useEffect(() => {
    if (!company?.id || !selectedListId) {
      return;
    }
    loadSubscribers(company.id, selectedListId);
  }, [company?.id, selectedListId, loadSubscribers]);

  useEffect(() => {
    if (selectedListId) {
      return;
    }
    if (defaultListId) {
      setSelectedListId(defaultListId);
      return;
    }
    if (listSetting?.sendgridListId) {
      setSelectedListId(listSetting.sendgridListId);
    }
  }, [defaultListId, listSetting?.sendgridListId, selectedListId]);

  useEffect(() => {
    if (!selectedListId) {
      return;
    }
    const selected = marketingLists.find((list) => list.id === selectedListId);
    if (selected) {
      setListName(selected.name);
      setListDescription(selected.description || "");
    }
  }, [selectedListId, marketingLists]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setImportContactSearchDebounced(importContactSearch);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [importContactSearch]);

  useEffect(() => {
    if (!company?.id || !importModalOpen || importTab !== "contacts") {
      return;
    }
    let active = true;
    setLoadingImportContacts(true);
    emailMarketingApiService
      .getImportableContacts(company.id, {
        sources: importSourceFilters.length ? importSourceFilters : undefined,
        search: importContactSearchDebounced.trim() || undefined,
      })
      .then((contacts) => {
        if (active) {
          setImportContactRows(contacts);
        }
      })
      .catch(() => {
        if (active) {
          setImportContactRows([]);
        }
      })
      .finally(() => {
        if (active) {
          setLoadingImportContacts(false);
        }
      });
    return () => {
      active = false;
    };
  }, [
    company?.id,
    importModalOpen,
    importTab,
    importSourceFilters,
    importContactSearchDebounced,
  ]);

  useEffect(() => {
    if (!listSetting) {
      return;
    }
    setListName(listSetting.listName);
    setListDescription(listSetting.description);
    if (listSetting.physicalAddress?.label) {
      setSelectedAddressLabel(listSetting.physicalAddress.label);
      setUseCustomAddress(false);
    }

    const allowedSenders = new Set(
      (config?.senders || []).map((sender) => sender.email)
    );
    const savedEmail = listSetting.fromEmail?.trim() || "";
    const activeEmail = config?.activeSender?.email?.trim() || "";

    if (savedEmail && allowedSenders.has(savedEmail)) {
      setFromEmail(savedEmail);
      setFromName(listSetting.fromName || savedEmail);
    } else if (activeEmail && allowedSenders.has(activeEmail)) {
      setFromEmail(activeEmail);
      setFromName(config?.activeSender?.name || activeEmail);
    } else {
      setFromEmail("");
      setFromName("");
    }
  }, [listSetting, config?.senders, config?.activeSender]);

  useEffect(() => {
    if (physicalAddressOptions?.current?.label) {
      setSelectedAddressLabel(physicalAddressOptions.current.label);
    }
  }, [physicalAddressOptions?.current?.label]);

  const senderOptions = config?.senders || [];

  const saveListSettings = async () => {
    if (!company?.id) {
      return;
    }
    await updateList(company.id, {
      listId: selectedListId || undefined,
      listName: listName.trim(),
      description: listDescription.trim(),
      fromEmail: fromEmail.trim(),
      fromName: fromName.trim() || fromEmail.trim(),
    });
    await loadMarketingLists(company.id);
  };

  const saveSenderSettings = async () => {
    if (!company?.id || !fromEmail.trim()) {
      toast.info(intl.formatMessage({ id: "app.emailMarketing.api.senderRequired" }));
      return;
    }
    await updateList(company.id, {
      fromEmail: fromEmail.trim(),
      fromName: fromName.trim() || fromEmail.trim(),
    });
  };

  const handleImportSubscribers = async () => {
    if (!company?.id) {
      return;
    }
    setImporting(true);
    try {
      if (importTab === "contacts") {
        if (selectedImportContactIds.length === 0) {
          toast.info(
            intl.formatMessage({ id: "app.emailMarketing.lists.importSelectRequired" })
          );
          return;
        }
        await importSubscribers(company.id, {
          contactIds: selectedImportContactIds,
        });
        setSelectedImportContactIds([]);
        setImportModalOpen(false);
      } else if (importTab === "paste") {
        if (!pasteValue.trim()) {
          toast.info(intl.formatMessage({ id: "app.emailMarketing.lists.importPasteRequired" }));
          return;
        }
        const result = await importSubscribersBulk(company.id, { paste: pasteValue });
        if (result) {
          setPasteValue("");
          setImportModalOpen(false);
        }
      } else if (importTab === "manual") {
        if (!manualEmail.trim()) {
          toast.info(intl.formatMessage({ id: "app.emailMarketing.api.senderRequired" }));
          return;
        }
        const subscriber = await createSubscriber(company.id, {
          email: manualEmail.trim(),
          name: manualName.trim(),
        });
        if (subscriber) {
          setManualEmail("");
          setManualName("");
          setImportModalOpen(false);
        }
      }
    } finally {
      setImporting(false);
    }
  };

  const selectableImportContacts = useMemo(
    () => importContactRows.filter((contact) => !contact.alreadySubscribed),
    [importContactRows]
  );

  const allSelectableImportSelected =
    selectableImportContacts.length > 0 &&
    selectableImportContacts.every((contact) =>
      selectedImportContactIds.includes(contact.id)
    );

  const toggleImportSourceFilter = (source: string) => {
    setImportSourceFilters((current) =>
      current.includes(source)
        ? current.filter((item) => item !== source)
        : [...current, source]
    );
    setSelectedImportContactIds([]);
  };

  const toggleSelectImportContact = (contactId: number) => {
    setSelectedImportContactIds((current) =>
      current.includes(contactId)
        ? current.filter((id) => id !== contactId)
        : [...current, contactId]
    );
  };

  const toggleSelectAllImportContacts = () => {
    if (allSelectableImportSelected) {
      setSelectedImportContactIds((current) =>
        current.filter(
          (id) => !selectableImportContacts.some((contact) => contact.id === id)
        )
      );
      return;
    }
    const ids = selectableImportContacts.map((contact) => contact.id);
    setSelectedImportContactIds((current) => {
      const merged = current.slice();
      ids.forEach((id) => {
        if (!merged.includes(id)) {
          merged.push(id);
        }
      });
      return merged;
    });
  };

  const handleDeleteSubscriber = async (email: string) => {
    if (!company?.id) {
      return;
    }
    if (
      !window.confirm(
        intl.formatMessage({ id: "app.emailMarketing.lists.deleteSubscriberConfirm" })
      )
    ) {
      return;
    }
    setDeletingSubscriberEmail(email);
    await deleteSubscriber(company.id, email, selectedListId || undefined);
    setDeletingSubscriberEmail(null);
  };

  const parseSpreadsheetRows = (rows: string[][]) => {
    if (!rows.length) {
      return [];
    }
    const header = rows[0].map((cell) => String(cell || "").trim().toLowerCase());
    const emailIndex = header.findIndex((cell) =>
      ["email", "correo", "e-mail", "mail"].includes(cell)
    );
    const nameIndex = header.findIndex((cell) =>
      ["name", "nombre", "full name", "nombre completo"].includes(cell)
    );
    const dataRows =
      emailIndex >= 0 || nameIndex >= 0 ? rows.slice(1) : rows;
    return dataRows
      .map((row) => {
        const email =
          emailIndex >= 0
            ? String(row[emailIndex] || "").trim()
            : String(row.find((cell) => String(cell).includes("@")) || row[0] || "").trim();
        const name =
          nameIndex >= 0
            ? String(row[nameIndex] || "").trim()
            : String(row.find((cell) => cell && !String(cell).includes("@")) || "").trim();
        return { email, name };
      })
      .filter((row) => row.email);
  };

  const handleImportFile = async (file: File) => {
    if (!company?.id) {
      return;
    }
    setImporting(true);
    try {
      const extension = file.name.split(".").pop()?.toLowerCase();
      let contacts: { email: string; name?: string }[] = [];

      if (extension === "csv" || extension === "txt") {
        const text = await file.text();
        const parsed = Papa.parse<string[]>(text, { skipEmptyLines: true });
        contacts = parseSpreadsheetRows(parsed.data as string[][]);
      } else if (extension === "xlsx" || extension === "xls") {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<string[]>(sheet, {
          header: 1,
          defval: "",
        }) as string[][];
        contacts = parseSpreadsheetRows(rows);
      } else {
        toast.info(intl.formatMessage({ id: "app.emailMarketing.lists.importFileInvalid" }));
        return;
      }

      if (!contacts.length) {
        toast.info(intl.formatMessage({ id: "app.emailMarketing.lists.importFileEmpty" }));
        return;
      }

      const result = await importSubscribersBulk(company.id, { contacts });
      if (result) {
        setImportModalOpen(false);
      }
    } finally {
      setImporting(false);
    }
  };

  const savePhysicalAddress = async () => {
    if (!company?.id) {
      return;
    }
    const label = useCustomAddress
      ? customAddressLine.trim()
      : selectedAddressLabel;
    if (!label) {
      toast.info(intl.formatMessage({ id: "app.emailMarketing.lists.addressRequired" }));
      return;
    }
    const selected = physicalAddressOptions?.options.find(
      (option) => option.label === label
    );
    await updateList(company.id, {
      physicalAddress: {
        label,
        line1: selected?.line1 || label,
        city: selected?.city || "",
        country: selected?.country || "",
        isCurrent: true,
      },
    });
  };

  const handleAddNotification = async () => {
    if (!company?.id || !notificationEmail.trim()) {
      return;
    }
    await addNotificationContact(company.id, {
      email: notificationEmail.trim(),
      name: notificationName.trim(),
    });
    setNotificationEmail("");
    setNotificationName("");
  };

  const displaySubscribers = apiSubscribers;
  const displaySuppressions = apiSuppressions;
  const notificationContacts = listSetting?.notificationContacts || [];
  const addressOptions = physicalAddressOptions?.options || [];
  const visibleTags = useMemo(() => {
    const query = tagSearch.trim().toLowerCase();
    return listTags.filter(
      (tag) => !query || tag.name.toLowerCase().includes(query)
    );
  }, [listTags, tagSearch]);

  const filteredFields = useMemo(() => {
    const query = fieldSearch.trim().toLowerCase();
    if (!query) {
      return customFields;
    }
    return customFields.filter((field) => field.name.toLowerCase().includes(query));
  }, [customFields, fieldSearch]);

  const tabItems = useMemo(
    () => [
      { id: "basic" as const, label: "app.emailMarketing.lists.tabs.basic" },
      {
        id: "subscribers" as const,
        label: "app.emailMarketing.lists.tabs.subscribers",
      },
      {
        id: "segments" as const,
        label: "app.emailMarketing.lists.tabs.segments",
      },
      {
        id: "personalize" as const,
        label: "app.emailMarketing.lists.tabs.personalize",
      },
      {
        id: "confirmation" as const,
        label: "app.emailMarketing.lists.tabs.confirmation",
      },
      {
        id: "suppressions" as const,
        label: "app.emailMarketing.lists.tabs.suppressions",
      },
    ],
    []
  );

  const filteredSubscribers = useMemo(() => {
    const query = subscriberSearch.trim().toLowerCase();
    let source = displaySubscribers;
    if (subscriberTagFilter) {
      source = source.filter(
        (subscriber) => subscriber.source === subscriberTagFilter
      );
    }
    if (!query) {
      return source;
    }
    return source.filter(
      (subscriber) =>
        subscriber.email.toLowerCase().includes(query) ||
        subscriber.name.toLowerCase().includes(query)
    );
  }, [subscriberSearch, subscriberTagFilter, displaySubscribers]);

  const activeSubscriberTag = useMemo(
    () =>
      subscriberTagFilter
        ? listTags.find((tag) => tag.nameId === subscriberTagFilter)
        : null,
    [listTags, subscriberTagFilter]
  );

  const handleCreateTag = async () => {
    if (!company?.id || !newTagName.trim()) {
      return;
    }
    const preset = TAG_COLOR_PRESETS[newTagColorIndex] || TAG_COLOR_PRESETS[0];
    const tag = await createListTag(company.id, {
      name: newTagName.trim(),
      textColor: preset.textColor,
      backgroundColor: preset.backgroundColor,
    });
    if (tag) {
      setTagModalOpen(false);
      setNewTagName("");
      setNewTagColorIndex(0);
    }
  };

  const handleImportTag = async (nameId: string, tagId: string) => {
    if (!company?.id) {
      return;
    }
    setTagImportingId(tagId);
    await importSubscribers(company.id, { sources: [nameId] });
    await loadListTags(company.id);
    setTagImportingId(null);
  };

  const viewSubscribersByTag = (nameId: string) => {
    setSubscriberTagFilter(nameId);
    setSubscriberSearch("");
    setActiveTab("subscribers");
  };

  const addCustomField = async () => {
    if (!newFieldName.trim() || !company?.id) {
      return;
    }
    const field = await createCustomField(company.id, {
      name: newFieldName.trim(),
      type: newFieldType,
    });
    if (field) {
      setFieldModalOpen(false);
      setNewFieldName("");
      await loadOverview(company.id);
    }
  };

  const addSegment = async () => {
    if (!newSegmentName.trim() || !company?.id) {
      return;
    }
    const segment = await createListSegment(company.id, {
      name: newSegmentName.trim(),
    });
    if (segment) {
      setSegmentModalOpen(false);
      setNewSegmentName("");
    }
  };

  const handleCreateList = async () => {
    if (!company?.id || !newListName.trim()) {
      return;
    }
    setCreatingList(true);
    const list = await createMarketingList(company.id, {
      name: newListName.trim(),
      description: newListDescription.trim(),
    });
    setCreatingList(false);
    if (!list) {
      return;
    }
    setCreateListModalOpen(false);
    setNewListName("");
    setNewListDescription("");
    setSelectedListId(list.id);
    await loadMarketingLists(company.id);
  };

  return (
    <Layout>
      <Content className="email-mkt">
        <section className="email-mkt__hero">
          <div className="email-mkt__hero-content">
            <div>
              <span className="email-mkt__eyebrow">
                <FormattedMessage id="app.sidebar.emailMarketing" />
              </span>
              <h1 className="email-mkt__title">
                <FormattedMessage id="app.emailMarketing.lists.title" />
              </h1>
              <p className="email-mkt__subtitle">
                <FormattedMessage id="app.emailMarketing.lists.subtitle" />
              </p>
            </div>

            <div className="email-mkt__stats">
              <div className="email-mkt__stat">
                <strong>{overview?.lists ?? 1}</strong>
                <span>
                  <FormattedMessage id="app.emailMarketing.lists.stats.lists" />
                </span>
              </div>
              <div className="email-mkt__stat">
                <strong>{overview?.subscribers ?? displaySubscribers.length}</strong>
                <span>
                  <FormattedMessage id="app.emailMarketing.lists.stats.subscribers" />
                </span>
              </div>
              <div className="email-mkt__stat">
                <strong>{overview?.fields ?? customFields.length}</strong>
                <span>
                  <FormattedMessage id="app.emailMarketing.lists.stats.fields" />
                </span>
              </div>
              <div className="email-mkt__stat">
                <strong>{overview?.tags ?? listTags.length}</strong>
                <span>
                  <FormattedMessage id="app.emailMarketing.lists.stats.tags" />
                </span>
              </div>
            </div>
          </div>
          <div className="email-mkt__engine-card">
            <span className="email-mkt__engine-label">
              <FormattedMessage id="app.emailMarketing.lists.engineLabel" />
            </span>
            <strong>
              <FormattedMessage id="app.emailMarketing.engine.name" />
            </strong>
            <small>
              <FormattedMessage id="app.emailMarketing.lists.engineHint" />
            </small>
          </div>
        </section>

        <section className="email-mkt__panel email-mkt__lists-picker-panel">
          <div className="email-mkt__lists-picker-head">
            <h2>
              <FormattedMessage id="app.emailMarketing.lists.myLists" />
            </h2>
            <p>
              <FormattedMessage id="app.emailMarketing.lists.myListsHelp" />
            </p>
          </div>
          <div className="email-mkt__lists-callout">
            <strong>
              <FormattedMessage id="app.emailMarketing.lists.purposeTitle" />
            </strong>
            <p>
              <FormattedMessage id="app.emailMarketing.lists.purposeHelp" />
            </p>
          </div>
          <div className="email-mkt__lists-picker-grid">
            {marketingLists.map((list) => (
              <button
                key={list.id}
                type="button"
                className={
                  selectedListId === list.id
                    ? "email-mkt__list-card is-active"
                    : "email-mkt__list-card"
                }
                onClick={() => setSelectedListId(list.id)}
              >
                <strong>{list.name}</strong>
                <span>
                  <FormattedMessage
                    id="app.emailMarketing.lists.subscriberCount"
                    values={{ count: list.subscribers }}
                  />
                </span>
                {list.isDefault && (
                  <small>
                    <FormattedMessage id="app.emailMarketing.lists.defaultList" />
                  </small>
                )}
              </button>
            ))}
            <button
              type="button"
              className="email-mkt__list-card email-mkt__list-card--create"
              onClick={() => setCreateListModalOpen(true)}
            >
              <span className="email-mkt__list-card-plus" aria-hidden="true">
                +
              </span>
              <strong>
                <FormattedMessage id="app.emailMarketing.lists.createList" />
              </strong>
              <span>
                <FormattedMessage id="app.emailMarketing.lists.createCardHelp" />
              </span>
            </button>
          </div>
        </section>

        <section className="email-mkt__panel">
          <nav className="email-mkt__tabs" aria-label="List settings">
            {tabItems.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={activeTab === tab.id ? "is-active" : ""}
                onClick={() => setActiveTab(tab.id)}
              >
                <FormattedMessage id={tab.label} />
              </button>
            ))}
          </nav>

          {activeTab === "basic" && (
            <div className="email-mkt__section-stack">
              <section className="email-mkt__section">
                <div className="email-mkt__section-title">
                  <span className="email-mkt__section-icon">01</span>
                  <h2>
                    <FormattedMessage id="app.emailMarketing.lists.nameTitle" />
                  </h2>
                </div>
                <p className="email-mkt__required">
                  <span>*</span>{" "}
                  <FormattedMessage id="app.emailMarketing.lists.required" />
                </p>
                <p className="email-mkt__copy">
                  <FormattedMessage id="app.emailMarketing.lists.nameHelp" />
                </p>

                <div className="email-mkt__inline">
                  <span>
                    <FormattedMessage id="app.emailMarketing.lists.uniqueId" />
                  </span>
                  <code>{selectedListId || listSetting?.uniqueId || "—"}</code>
                </div>

                <div className="email-mkt__form-grid email-mkt__form-grid--single">
                  <label className="email-mkt__field">
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.listName" /> *
                    </span>
                    <input
                      value={listName}
                      onChange={(event) => setListName(event.target.value)}
                      maxLength={32}
                    />
                    <small>
                      <FormattedMessage id="app.emailMarketing.lists.nameLimit" />
                    </small>
                  </label>

                  <label className="email-mkt__field">
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.description" /> *
                    </span>
                    <textarea
                      value={listDescription}
                      onChange={(event) => setListDescription(event.target.value)}
                      maxLength={400}
                      rows={3}
                    />
                    <small>
                      <FormattedMessage id="app.emailMarketing.lists.descriptionLimit" />
                    </small>
                  </label>
                </div>
                <button
                  type="button"
                  className="email-mkt__primary-btn email-mkt__mt-sm"
                  onClick={saveListSettings}
                >
                  <FormattedMessage id="app.emailMarketing.lists.saveList" />
                </button>
              </section>

              <section className="email-mkt__section">
                <div className="email-mkt__section-title">
                  <span className="email-mkt__section-icon">02</span>
                  <h2>
                    <FormattedMessage id="app.emailMarketing.lists.senderTitle" />
                  </h2>
                </div>
                    <p className="email-mkt__copy">
                      <FormattedMessage id="app.emailMarketing.lists.senderHelp" />
                    </p>
                    {senderOptions.length === 0 && (
                      <p className="email-mkt__note">
                        <FormattedMessage id="app.emailMarketing.lists.noSendersConfigured" />
                      </p>
                    )}
                <div className="email-mkt__form-grid">
                  <label className="email-mkt__field">
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.fromAddress" /> *
                    </span>
                    <select
                      value={fromEmail}
                      onChange={(event) => {
                        const nextEmail = event.target.value;
                        setFromEmail(nextEmail);
                        const selected = senderOptions.find(
                          (sender) => sender.email === nextEmail
                        );
                        if (selected?.name) {
                          setFromName(selected.name);
                        }
                      }}
                    >
                      {senderOptions.length === 0 ? (
                        <option value="">
                          {intl.formatMessage({
                            id: "app.emailMarketing.config.addSender",
                          })}
                        </option>
                      ) : null}
                      {senderOptions.map((sender) => (
                        <option key={sender.email} value={sender.email}>
                          {sender.email}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="email-mkt__field">
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.fromName" />
                    </span>
                    <input
                      value={fromName}
                      onChange={(event) => setFromName(event.target.value)}
                    />
                  </label>
                </div>
                <button
                  type="button"
                  className="email-mkt__primary-btn email-mkt__mt-sm"
                  onClick={saveSenderSettings}
                >
                  <FormattedMessage id="app.emailMarketing.lists.saveSender" />
                </button>
              </section>

              <section className="email-mkt__section">
                <div className="email-mkt__section-title">
                  <span className="email-mkt__section-icon">03</span>
                  <h2>
                    <FormattedMessage id="app.emailMarketing.lists.contactAddressTitle" />
                  </h2>
                </div>
                <p className="email-mkt__copy">
                  <FormattedMessage id="app.emailMarketing.lists.contactAddressHelp" />
                </p>
                <div className="email-mkt__radio-list">
                  {addressOptions.map((address) => (
                    <label key={address.label}>
                      <input
                        type="radio"
                        name="email-mkt-address"
                        checked={
                          !useCustomAddress && selectedAddressLabel === address.label
                        }
                        onChange={() => {
                          setUseCustomAddress(false);
                          setSelectedAddressLabel(address.label);
                        }}
                      />
                      <span>{address.label}</span>
                      {listSetting?.physicalAddress?.label === address.label && (
                        <strong>
                          <FormattedMessage id="app.emailMarketing.lists.current" />
                        </strong>
                      )}
                    </label>
                  ))}
                  {addressOptions.length === 0 && (
                    <p className="email-mkt__note">
                      <FormattedMessage id="app.emailMarketing.lists.noAddressOptions" />
                    </p>
                  )}
                  <label>
                    <input
                      type="radio"
                      name="email-mkt-address"
                      checked={useCustomAddress}
                      onChange={() => setUseCustomAddress(true)}
                    />
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.useDifferentAddress" />
                    </span>
                  </label>
                  {useCustomAddress && (
                    <label className="email-mkt__field">
                      <input
                        value={customAddressLine}
                        onChange={(event) => setCustomAddressLine(event.target.value)}
                        placeholder={intl.formatMessage({
                          id: "app.emailMarketing.lists.customAddressPlaceholder",
                        })}
                      />
                    </label>
                  )}
                </div>
                <button
                  type="button"
                  className="email-mkt__primary-btn email-mkt__mt-sm"
                  onClick={savePhysicalAddress}
                >
                  <FormattedMessage id="app.emailMarketing.lists.saveAddress" />
                </button>
              </section>

              <section className="email-mkt__section">
                <div className="email-mkt__section-title">
                  <span className="email-mkt__section-icon">04</span>
                  <h2>
                    <FormattedMessage id="app.emailMarketing.lists.notificationsTitle" />
                  </h2>
                </div>
                <p className="email-mkt__copy">
                  <FormattedMessage id="app.emailMarketing.lists.notificationsHelp" />
                </p>
                <div className="email-mkt__add-row">
                  <label className="email-mkt__field">
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.email" /> *
                    </span>
                    <input
                      placeholder="name@example.com"
                      value={notificationEmail}
                      onChange={(event) => setNotificationEmail(event.target.value)}
                    />
                  </label>
                  <label className="email-mkt__field">
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.nameOptional" />
                    </span>
                    <input
                      placeholder={intl.formatMessage({
                        id: "app.emailMarketing.lists.namePlaceholder",
                      })}
                      value={notificationName}
                      onChange={(event) => setNotificationName(event.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="email-mkt__primary-btn"
                    onClick={handleAddNotification}
                  >
                    <FormattedMessage id="app.emailMarketing.lists.add" />
                  </button>
                </div>
                <div className="email-mkt__table">
                  <div className="email-mkt__table-row email-mkt__table-row--head">
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.email" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.name" />
                    </span>
                    <span />
                  </div>
                  {notificationContacts.map((contact) => (
                    <div className="email-mkt__table-row" key={contact.email}>
                      <strong>{contact.email}</strong>
                      <span>{contact.name}</span>
                      <button
                        type="button"
                        className="email-mkt__link-btn"
                        onClick={() =>
                          company?.id && removeNotificationContact(company.id, contact.email)
                        }
                      >
                        <FormattedMessage id="app.emailMarketing.lists.remove" />
                      </button>
                    </div>
                  ))}
                  {notificationContacts.length === 0 && (
                    <p className="email-mkt__note">
                      <FormattedMessage id="app.emailMarketing.lists.notificationsEmpty" />
                    </p>
                  )}
                </div>
              </section>
            </div>
          )}

          {activeTab === "personalize" && (
            <div className="email-mkt__section-stack">
              <section className="email-mkt__section">
                <div className="email-mkt__section-header">
                  <div>
                    <div className="email-mkt__section-title">
                      <span className="email-mkt__section-icon">01</span>
                      <h2>
                        <FormattedMessage id="app.emailMarketing.lists.customFieldsTitle" />
                      </h2>
                    </div>
                    <p className="email-mkt__note">
                      <FormattedMessage id="app.emailMarketing.lists.customFieldsLimit" />
                    </p>
                  </div>
                  <button type="button" className="email-mkt__primary-btn" onClick={() => setFieldModalOpen(true)}>
                    <FormattedMessage id="app.emailMarketing.lists.addCustomField" />
                  </button>
                </div>

                <div className="email-mkt__toolbar">
                  <input
                    value={fieldSearch}
                    onChange={(event) => setFieldSearch(event.target.value)}
                    placeholder={intl.formatMessage({
                      id: "app.emailMarketing.lists.searchCustomFields",
                    })}
                  />
                </div>

                <div className="email-mkt__table email-mkt__table--actions">
                  <div className="email-mkt__table-row email-mkt__table-row--head">
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.customField" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.fieldType" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.subscriberCanUpdate" />
                    </span>
                    <span />
                  </div>
                  {filteredFields.map((field) => (
                    <div className="email-mkt__table-row" key={field.id}>
                      <strong>{field.name}</strong>
                      <span>
                        <FormattedMessage id={fieldTypeLabel(field.type)} />
                      </span>
                      <span>
                        <FormattedMessage
                          id={
                            field.subscriberCanUpdate
                              ? "app.common.yes"
                              : "app.common.no"
                          }
                        />
                      </span>
                      <span />
                    </div>
                  ))}
                  {filteredFields.length === 0 && (
                    <div className="email-mkt__empty-row">
                      <FormattedMessage id="app.emailMarketing.lists.emptyFields" />
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}

          {activeTab === "confirmation" && (
            <div className="email-mkt__section-stack">
              <section className="email-mkt__section">
                <div className="email-mkt__section-header">
                  <div>
                    <div className="email-mkt__section-title">
                      <span className="email-mkt__section-icon">01</span>
                      <h2>
                        <FormattedMessage id="app.emailMarketing.lists.tagsTitle" />
                      </h2>
                    </div>
                    <p className="email-mkt__note">
                      <FormattedMessage id="app.emailMarketing.lists.tagsHelp" />
                    </p>
                    <p className="email-mkt__copy">
                      <FormattedMessage id="app.emailMarketing.lists.tagsFromContactsHelp" />
                    </p>
                  </div>
                  <button
                    type="button"
                    className="email-mkt__primary-btn"
                    onClick={() => setTagModalOpen(true)}
                  >
                    <FormattedMessage id="app.emailMarketing.lists.createTag" />
                  </button>
                </div>

                <div className="email-mkt__toolbar email-mkt__toolbar--split">
                  <input
                    value={tagSearch}
                    onChange={(event) => setTagSearch(event.target.value)}
                    placeholder={intl.formatMessage({
                      id: "app.emailMarketing.lists.search",
                    })}
                  />
                </div>

                <div className="email-mkt__table email-mkt__table--tags">
                  <div className="email-mkt__table-row email-mkt__table-row--head">
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.tagName" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.tagCrmContacts" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.tagOnList" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.tagActions" />
                    </span>
                  </div>
                  {visibleTags.map((tag) => (
                    <div className="email-mkt__table-row" key={tag.id}>
                      <span>
                        <span
                          className="email-mkt__tag-pill"
                          style={{
                            color: tag.textColor || "#1e40af",
                            backgroundColor: tag.backgroundColor || "#dbeafe",
                          }}
                        >
                          {tag.name}
                        </span>
                      </span>
                      <span>{tag.crmContacts ?? tag.subscribers}</span>
                      <button
                        type="button"
                        className="email-mkt__link-btn"
                        onClick={() => viewSubscribersByTag(tag.nameId)}
                      >
                        {tag.listSubscribers ?? 0}
                      </button>
                      <span className="email-mkt__tag-actions">
                        <button
                          type="button"
                          className="email-mkt__ghost-btn email-mkt__tag-action-btn"
                          disabled={tagImportingId === tag.id}
                          onClick={() => handleImportTag(tag.nameId, tag.id)}
                        >
                          {tagImportingId === tag.id ? (
                            <FormattedMessage id="app.emailMarketing.lists.tagImporting" />
                          ) : (
                            <FormattedMessage id="app.emailMarketing.lists.tagImport" />
                          )}
                        </button>
                        <button
                          type="button"
                          className="email-mkt__link-btn"
                          onClick={() => viewSubscribersByTag(tag.nameId)}
                        >
                          <FormattedMessage id="app.emailMarketing.lists.tags.viewSubscribers" />
                        </button>
                      </span>
                    </div>
                  ))}
                  {visibleTags.length === 0 && (
                    <div className="email-mkt__empty-row email-mkt__empty-row--tags">
                      <p>
                        <FormattedMessage id="app.emailMarketing.lists.emptyTags" />
                      </p>
                      <button
                        type="button"
                        className="email-mkt__primary-btn"
                        onClick={() => setTagModalOpen(true)}
                      >
                        <FormattedMessage id="app.emailMarketing.lists.createTag" />
                      </button>
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}

          {activeTab === "subscribers" && (
            <div className="email-mkt__section-stack">
              <section className="email-mkt__section">
                <div className="email-mkt__section-header">
                  <div>
                    <div className="email-mkt__section-title">
                      <span className="email-mkt__section-icon">01</span>
                      <h2>
                        <FormattedMessage id="app.emailMarketing.lists.subscribersTitle" />
                      </h2>
                    </div>
                    <p className="email-mkt__note">
                      <FormattedMessage id="app.emailMarketing.lists.subscribersHelp" />
                    </p>
                    <p className="email-mkt__copy">
                      <FormattedMessage id="app.emailMarketing.lists.importFromContactsHelp" />
                    </p>
                  </div>
                  <button
                    type="button"
                    className="email-mkt__primary-btn"
                    onClick={() => setImportModalOpen(true)}
                  >
                    <FormattedMessage id="app.emailMarketing.lists.importSubscribers" />
                  </button>
                </div>
                <div className="email-mkt__toolbar">
                  {activeSubscriberTag && (
                    <div className="email-mkt__filter-chip">
                      <span
                        className="email-mkt__tag-pill"
                        style={{
                          color: activeSubscriberTag.textColor || "#1e40af",
                          backgroundColor:
                            activeSubscriberTag.backgroundColor || "#dbeafe",
                        }}
                      >
                        {activeSubscriberTag.name}
                      </span>
                      <button
                        type="button"
                        className="email-mkt__filter-chip-clear"
                        onClick={() => setSubscriberTagFilter(null)}
                      >
                        <FormattedMessage id="app.emailMarketing.lists.clearTagFilter" />
                      </button>
                    </div>
                  )}
                  <input
                    value={subscriberSearch}
                    onChange={(event) => setSubscriberSearch(event.target.value)}
                    placeholder={intl.formatMessage({
                      id: "app.emailMarketing.lists.searchSubscribers",
                    })}
                  />
                </div>
                <div className="email-mkt__table email-mkt__table--actions">
                  <div className="email-mkt__table-row email-mkt__table-row--head">
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.email" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.name" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.updatedAt" />
                    </span>
                    <span />
                  </div>
                  {filteredSubscribers.map((subscriber) => (
                    <div className="email-mkt__table-row" key={subscriber.email}>
                      <strong>{subscriber.email}</strong>
                      <span>{subscriber.name}</span>
                      <span>{subscriber.updatedAt}</span>
                      <button
                        type="button"
                        className="email-mkt__icon-btn email-mkt__icon-btn--danger"
                        onClick={() => handleDeleteSubscriber(subscriber.email)}
                        disabled={deletingSubscriberEmail === subscriber.email}
                        aria-label={intl.formatMessage({
                          id: "app.emailMarketing.lists.deleteSubscriber",
                        })}
                        title={intl.formatMessage({
                          id: "app.emailMarketing.lists.deleteSubscriber",
                        })}
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </div>
                  ))}
                  {filteredSubscribers.length === 0 && (
                    <div className="email-mkt__empty-row">
                      <FormattedMessage id="app.emailMarketing.lists.emptySubscribers" />
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}

          {activeTab === "segments" && (
            <div className="email-mkt__section-stack">
              <section className="email-mkt__section">
                <div className="email-mkt__section-header">
                  <div>
                    <div className="email-mkt__section-title">
                      <span className="email-mkt__section-icon">01</span>
                      <h2>
                        <FormattedMessage id="app.emailMarketing.lists.segmentsTitle" />
                      </h2>
                    </div>
                    <p className="email-mkt__note">
                      <FormattedMessage id="app.emailMarketing.lists.segmentsHelp" />
                    </p>
                  </div>
                  <button type="button" className="email-mkt__primary-btn" onClick={() => setSegmentModalOpen(true)}>
                    <FormattedMessage id="app.emailMarketing.lists.createSegment" />
                  </button>
                </div>
                <div className="email-mkt__table email-mkt__table--actions">
                  <div className="email-mkt__table-row email-mkt__table-row--head">
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.segmentName" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.subscribers" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.emails.status" />
                    </span>
                    <span />
                  </div>
                  {listSegments.map((segment) => (
                    <div className="email-mkt__table-row" key={segment.id}>
                      <strong>{segment.name}</strong>
                      <span>{segment.contacts}</span>
                      <span className="email-mkt__pill">
                        <FormattedMessage
                          id={
                            segment.status === "active"
                              ? "app.emailMarketing.lists.segmentActive"
                              : "app.emailMarketing.lists.segmentDraft"
                          }
                        />
                      </span>
                      <span />
                    </div>
                  ))}
                  {listSegments.length === 0 && (
                    <div className="email-mkt__empty-row">
                      <FormattedMessage id="app.emailMarketing.lists.emptySegments" />
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}

          {activeTab === "suppressions" && (
            <div className="email-mkt__section-stack">
              <section className="email-mkt__section">
                <div className="email-mkt__section-title">
                  <span className="email-mkt__section-icon">01</span>
                  <h2>
                    <FormattedMessage id="app.emailMarketing.lists.suppressionsTitle" />
                  </h2>
                </div>
                <p className="email-mkt__copy">
                    <FormattedMessage id="app.emailMarketing.lists.suppressionsHelp" />
                </p>
                <div className="email-mkt__table">
                  <div className="email-mkt__table-row email-mkt__table-row--head">
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.email" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.suppressionReason" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.emails.date" />
                    </span>
                  </div>
                  {displaySuppressions.map((entry) => (
                    <div className="email-mkt__table-row" key={entry.email}>
                      <strong>{entry.email}</strong>
                      <span>{entry.reason}</span>
                      <span>{entry.date}</span>
                    </div>
                  ))}
                  {displaySuppressions.length === 0 && (
                    <div className="email-mkt__empty-row">
                      <FormattedMessage id="app.emailMarketing.lists.emptySuppressions" />
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}
        </section>

        <EmailMktModal
          open={importModalOpen}
          title={<FormattedMessage id="app.emailMarketing.lists.importSubscribers" />}
          onClose={() => {
            setImportModalOpen(false);
            setSelectedImportContactIds([]);
            setImportContactSearch("");
            setImportSourceFilters([]);
          }}
          footer={
            importTab !== "file" ? (
              <button
                type="button"
                className="email-mkt__primary-btn"
                onClick={handleImportSubscribers}
                disabled={importing}
              >
                <FormattedMessage
                  id={
                    importTab === "manual"
                      ? "app.emailMarketing.lists.importAddContact"
                      : "app.emailMarketing.lists.importStart"
                  }
                />
              </button>
            ) : null
          }
        >
          <div className="email-mkt__segmented email-mkt__mt-sm">
            <button
              type="button"
              className={importTab === "contacts" ? "is-active" : ""}
              onClick={() => setImportTab("contacts")}
            >
              <FormattedMessage id="app.emailMarketing.lists.importTabContacts" />
            </button>
            <button
              type="button"
              className={importTab === "file" ? "is-active" : ""}
              onClick={() => setImportTab("file")}
            >
              <FormattedMessage id="app.emailMarketing.lists.importTabFile" />
            </button>
            <button
              type="button"
              className={importTab === "paste" ? "is-active" : ""}
              onClick={() => setImportTab("paste")}
            >
              <FormattedMessage id="app.emailMarketing.lists.importTabPaste" />
            </button>
            <button
              type="button"
              className={importTab === "manual" ? "is-active" : ""}
              onClick={() => setImportTab("manual")}
            >
              <FormattedMessage id="app.emailMarketing.lists.importTabManual" />
            </button>
          </div>

          {importTab === "contacts" && (
            <>
              <p className="email-mkt__copy">
                <FormattedMessage id="app.emailMarketing.lists.importHelpSelect" />
              </p>
              <div className="email-mkt__import-filters">
                <div className="email-mkt__import-source-chips">
                  {ContactSourceOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={
                        importSourceFilters.includes(option.value) ? "is-active" : ""
                      }
                      onClick={() => toggleImportSourceFilter(option.value)}
                    >
                      {intl.formatMessage({ id: option.label })}
                    </button>
                  ))}
                </div>
                <input
                  value={importContactSearch}
                  onChange={(event) => setImportContactSearch(event.target.value)}
                  placeholder={intl.formatMessage({
                    id: "app.emailMarketing.lists.searchImportContacts",
                  })}
                />
              </div>
              {selectedImportContactIds.length > 0 && (
                <p className="email-mkt__import-selected-count">
                  <FormattedMessage
                    id="app.emailMarketing.lists.importSelectedCount"
                    values={{ count: selectedImportContactIds.length }}
                  />
                </p>
              )}
              <div className="email-mkt__import-table-wrap">
                <div className="email-mkt__table email-mkt__table--import">
                  <div className="email-mkt__table-row email-mkt__table-row--head">
                    <span className="email-mkt__row-check">
                      {selectableImportContacts.length > 0 ? (
                        <input
                          type="checkbox"
                          checked={allSelectableImportSelected}
                          onChange={toggleSelectAllImportContacts}
                          aria-label={intl.formatMessage({
                            id: "app.emailMarketing.lists.importSelectAll",
                          })}
                        />
                      ) : null}
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.email" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.name" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.source" />
                    </span>
                  </div>
                  {loadingImportContacts ? (
                    <div className="email-mkt__empty-row">
                      <FormattedMessage id="app.emailMarketing.lists.importLoading" />
                    </div>
                  ) : importContactRows.length > 0 ? (
                    importContactRows.map((contact) => (
                      <div className="email-mkt__table-row" key={contact.id}>
                        <span className="email-mkt__row-check">
                          {contact.alreadySubscribed ? (
                            <span className="email-mkt__import-badge">
                              <FormattedMessage
                                id="app.emailMarketing.lists.alreadySubscribed"
                              />
                            </span>
                          ) : (
                            <input
                              type="checkbox"
                              checked={selectedImportContactIds.includes(contact.id)}
                              onChange={() => toggleSelectImportContact(contact.id)}
                              aria-label={intl.formatMessage({
                                id: "app.emailMarketing.lists.importSelectOne",
                              })}
                            />
                          )}
                        </span>
                        <strong>{contact.email}</strong>
                        <span>{contact.name}</span>
                        <span>{sourceLabel(contact.source, intl)}</span>
                      </div>
                    ))
                  ) : (
                    <div className="email-mkt__empty-row">
                      <FormattedMessage id="app.emailMarketing.lists.importNoContacts" />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {importTab === "file" && (
            <>
              <p className="email-mkt__copy">
                <FormattedMessage id="app.emailMarketing.lists.importFileHelp" />
              </p>
              <label className="email-mkt__field">
                <span>
                  <FormattedMessage id="app.emailMarketing.lists.importFile" />
                </span>
                <input
                  type="file"
                  accept=".csv,.txt,.xlsx,.xls"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) {
                      handleImportFile(file);
                      event.target.value = "";
                    }
                  }}
                  disabled={importing}
                />
              </label>
            </>
          )}

          {importTab === "paste" && (
            <>
              <p className="email-mkt__copy">
                <FormattedMessage id="app.emailMarketing.lists.importPasteHelp" />
              </p>
              <label className="email-mkt__field">
                <textarea
                  rows={6}
                  value={pasteValue}
                  onChange={(event) => setPasteValue(event.target.value)}
                  placeholder="email@empresa.com, Nombre&#10;otro@empresa.com	Nombre Apellido"
                />
              </label>
            </>
          )}

          {importTab === "manual" && (
            <>
              <p className="email-mkt__copy">
                <FormattedMessage id="app.emailMarketing.lists.importManualHelp" />
              </p>
              <label className="email-mkt__field">
                <span>
                  <FormattedMessage id="app.emailMarketing.lists.email" /> *
                </span>
                <input
                  value={manualEmail}
                  onChange={(event) => setManualEmail(event.target.value)}
                  placeholder="name@example.com"
                />
              </label>
              <label className="email-mkt__field">
                <span>
                  <FormattedMessage id="app.emailMarketing.lists.nameOptional" />
                </span>
                <input
                  value={manualName}
                  onChange={(event) => setManualName(event.target.value)}
                />
              </label>
            </>
          )}
        </EmailMktModal>

        <EmailMktModal
          open={fieldModalOpen}
          title={<FormattedMessage id="app.emailMarketing.lists.addCustomField" />}
          onClose={() => setFieldModalOpen(false)}
          footer={
            <button type="button" className="email-mkt__primary-btn" onClick={addCustomField}>
              <FormattedMessage id="app.emailMarketing.lists.add" />
            </button>
          }
        >
          <label className="email-mkt__field">
            <span>
              <FormattedMessage id="app.emailMarketing.lists.customField" />
            </span>
            <input
              value={newFieldName}
              onChange={(event) => setNewFieldName(event.target.value)}
            />
          </label>
          <label className="email-mkt__field">
            <span>
              <FormattedMessage id="app.emailMarketing.lists.fieldType" />
            </span>
            <select
              value={newFieldType}
              onChange={(event) =>
                setNewFieldType(event.target.value as "text" | "number" | "date")
              }
            >
              <option value="text">
                {intl.formatMessage({ id: "app.emailMarketing.lists.fieldType.text" })}
              </option>
              <option value="number">
                {intl.formatMessage({ id: "app.emailMarketing.lists.fieldType.number" })}
              </option>
              <option value="date">
                {intl.formatMessage({ id: "app.emailMarketing.lists.fieldType.date" })}
              </option>
            </select>
          </label>
        </EmailMktModal>

        <EmailMktModal
          open={segmentModalOpen}
          title={<FormattedMessage id="app.emailMarketing.lists.createSegment" />}
          onClose={() => setSegmentModalOpen(false)}
          footer={
            <button type="button" className="email-mkt__primary-btn" onClick={addSegment}>
              <FormattedMessage id="app.emailMarketing.lists.add" />
            </button>
          }
        >
          <label className="email-mkt__field">
            <span>
              <FormattedMessage id="app.emailMarketing.lists.segmentName" />
            </span>
            <input
              value={newSegmentName}
              onChange={(event) => setNewSegmentName(event.target.value)}
            />
          </label>
          <p className="email-mkt__copy">
            <FormattedMessage id="app.emailMarketing.lists.segmentsHelp" />
          </p>
        </EmailMktModal>

        <EmailMktModal
          open={tagModalOpen}
          title={<FormattedMessage id="app.emailMarketing.lists.createTag" />}
          onClose={() => setTagModalOpen(false)}
          footer={
            <button type="button" className="email-mkt__primary-btn" onClick={handleCreateTag}>
              <FormattedMessage id="app.emailMarketing.lists.add" />
            </button>
          }
        >
          <label className="email-mkt__field">
            <span>
              <FormattedMessage id="app.emailMarketing.lists.tagName" />
            </span>
            <input
              value={newTagName}
              onChange={(event) => setNewTagName(event.target.value)}
              placeholder={intl.formatMessage({
                id: "app.emailMarketing.lists.tagNamePlaceholder",
              })}
            />
          </label>
          <div className="email-mkt__tag-color-picker">
            <span>
              <FormattedMessage id="app.emailMarketing.lists.tagColor" />
            </span>
            <div className="email-mkt__tag-color-options">
              {TAG_COLOR_PRESETS.map((preset, index) => (
                <button
                  key={preset.backgroundColor}
                  type="button"
                  className={
                    newTagColorIndex === index
                      ? "email-mkt__tag-color-option is-selected"
                      : "email-mkt__tag-color-option"
                  }
                  style={{
                    color: preset.textColor,
                    backgroundColor: preset.backgroundColor,
                  }}
                  onClick={() => setNewTagColorIndex(index)}
                  aria-label={intl.formatMessage({
                    id: "app.emailMarketing.lists.tagColorOption",
                  })}
                >
                  {newTagName.trim() || intl.formatMessage({
                    id: "app.emailMarketing.lists.tagPreview",
                  })}
                </button>
              ))}
            </div>
          </div>
          <p className="email-mkt__copy">
            <FormattedMessage id="app.emailMarketing.lists.createTagHelp" />
          </p>
        </EmailMktModal>

        <EmailMktModal
          open={createListModalOpen}
          title={<FormattedMessage id="app.emailMarketing.lists.createListTitle" />}
          onClose={() => {
            if (!creatingList) {
              setCreateListModalOpen(false);
            }
          }}
          footer={
            <>
              <button
                type="button"
                className="email-mkt__ghost-btn"
                onClick={() => setCreateListModalOpen(false)}
                disabled={creatingList}
              >
                <FormattedMessage id="app.emailMarketing.emails.wizard.close" />
              </button>
              <button
                type="button"
                className="email-mkt__primary-btn"
                onClick={handleCreateList}
                disabled={creatingList || !newListName.trim()}
              >
                <FormattedMessage id="app.emailMarketing.lists.createList" />
              </button>
            </>
          }
        >
          <p className="email-mkt__copy">
            <FormattedMessage id="app.emailMarketing.lists.createListModalHelp" />
          </p>
          <label className="email-mkt__field">
            <span>
              <FormattedMessage id="app.emailMarketing.lists.listName" />
            </span>
            <input
              value={newListName}
              onChange={(event) => setNewListName(event.target.value)}
              maxLength={32}
            />
          </label>
          <label className="email-mkt__field">
            <span>
              <FormattedMessage id="app.emailMarketing.lists.description" />
            </span>
            <textarea
              rows={3}
              value={newListDescription}
              onChange={(event) => setNewListDescription(event.target.value)}
            />
          </label>
        </EmailMktModal>
      </Content>
    </Layout>
  );
};

type DateSortDirection = "asc" | "desc";

export const EmailMarketingEmailsScreen = () => {
  const intl = useIntl();
  const { company, user } = useMeCore();
  const {
    campaigns: apiCampaigns,
    overview,
    config,
    listSetting,
    loadCampaigns,
    loadOverview,
    loadConfig,
    loadList,
    createCampaign: createCampaignApi,
    updateCampaign: updateCampaignApi,
    deleteCampaign: deleteCampaignApi,
    deleteCampaignsBulk,
    cancelScheduleCampaign,
    getCampaign,
    sendTest,
    scheduleCampaign,
    sendCampaignNow,
    templates: apiTemplates,
    loadTemplates,
    getTemplate,
    listTags,
    marketingLists,
    defaultListId,
    loadListTags,
    loadMarketingLists,
  } = useEmailMarketingCore();
  const {
    goToEmailMarketingCampaign,
    goToEmailMarketingTemplates,
    goToEmailMarketingEmailsFromTemplate,
    history,
    routes,
    search,
  } = useRouterCore();
  const [activeTab, setActiveTab] = useState<EmailTab>("all");
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [openActionId, setOpenActionId] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerCampaignId, setComposerCampaignId] = useState<string | null>(null);
  const [composerSubject, setComposerSubject] = useState("");
  const [composerHtml, setComposerHtml] = useState(DEFAULT_RICH_HTML);
  const [composerSaving, setComposerSaving] = useState(false);
  const [createType, setCreateType] = useState<CreateType>("plainText");
  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [dateSortDirection, setDateSortDirection] = useState<DateSortDirection>("desc");
  const [copyListHoverId, setCopyListHoverId] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [testModalCampaign, setTestModalCampaign] = useState<{
    id: string;
    html?: string;
  } | null>(null);
  const [testEmailInput, setTestEmailInput] = useState("");
  const [testTagsInput, setTestTagsInput] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [scheduleModalCampaign, setScheduleModalCampaign] = useState<{
    id: string;
    subject: string;
  } | null>(null);
  const [scheduleMode, setScheduleMode] = useState<"now" | "later">("later");
  const [scheduleDateTime, setScheduleDateTime] = useState(defaultScheduleDateTime());
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [composerTargetListId, setComposerTargetListId] = useState<string | null>(null);
  const CREATE_MENU_WIDTH = 360;
  const CREATE_MENU_ID = "email-mkt-create-menu";
  const createMenuRef = useRef<HTMLDivElement>(null);
  const createWrapRef = useRef<HTMLDivElement>(null);
  const [createMenuPosition, setCreateMenuPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const updateCreateMenuPosition = useCallback(() => {
    if (!createWrapRef.current) {
      return;
    }
    const rect = createWrapRef.current.getBoundingClientRect();
    const width = Math.min(CREATE_MENU_WIDTH, window.innerWidth - 32);
    let left = rect.right - width;
    left = Math.max(16, Math.min(left, window.innerWidth - width - 16));
    setCreateMenuPosition({
      top: rect.bottom + 8,
      left,
      width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!createMenuOpen) {
      setCreateMenuPosition(null);
      return undefined;
    }
    updateCreateMenuPosition();
    window.addEventListener("resize", updateCreateMenuPosition);
    window.addEventListener("scroll", updateCreateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateCreateMenuPosition);
      window.removeEventListener("scroll", updateCreateMenuPosition, true);
    };
  }, [createMenuOpen, updateCreateMenuPosition]);

  useEffect(() => {
    if (!createMenuOpen) {
      return undefined;
    }
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const portalMenu = document.getElementById(CREATE_MENU_ID);
      if (
        createMenuRef.current?.contains(target) ||
        portalMenu?.contains(target)
      ) {
        return;
      }
      setCreateMenuOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [createMenuOpen]);

  useEffect(() => {
    if (!openActionId) {
      return undefined;
    }
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest(".email-mkt__row-actions")) {
        return;
      }
      setOpenActionId(null);
      setCopyListHoverId(null);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [openActionId]);

  const templateIdFromUrl = useMemo(() => {
    return new URLSearchParams(search).get("templateId");
  }, [search]);

  const editCampaignIdFromUrl = useMemo(() => {
    return new URLSearchParams(search).get("edit");
  }, [search]);

  useEffect(() => {
    if (!company?.id) {
      return;
    }
    loadCampaigns(company.id);
    loadOverview(company.id);
    loadConfig(company.id);
    loadList(company.id);
    loadTemplates(company.id);
    loadListTags(company.id);
    loadMarketingLists(company.id);
  }, [
    company?.id,
    loadCampaigns,
    loadOverview,
    loadConfig,
    loadList,
    loadTemplates,
    loadListTags,
    loadMarketingLists,
  ]);

  useEffect(() => {
    if (composerTargetListId || !defaultListId) {
      return;
    }
    setComposerTargetListId(defaultListId);
  }, [composerTargetListId, defaultListId]);

  useEffect(() => {
    if (!composerOpen || !company?.id) {
      return;
    }
    loadMarketingLists(company.id);
  }, [composerOpen, company?.id, loadMarketingLists]);

  const composerListOptions = useMemo(() => {
    if (marketingLists.length > 0) {
      return marketingLists;
    }
    const listId = defaultListId || listSetting?.sendgridListId;
    if (!listId) {
      return [];
    }
    return [
      {
        id: listId,
        name: listSetting?.listName || "Wembii",
        subscribers: overview?.subscribers ?? 0,
      },
    ];
  }, [
    marketingLists,
    defaultListId,
    listSetting?.sendgridListId,
    listSetting?.listName,
    overview?.subscribers,
  ]);

  useEffect(() => {
    if (!composerOpen || composerTargetListId || composerListOptions.length === 0) {
      return;
    }
    setComposerTargetListId(composerListOptions[0].id);
  }, [composerOpen, composerTargetListId, composerListOptions]);

  useEffect(() => {
    if (!company?.id || !templateIdFromUrl) {
      return;
    }
    let cancelled = false;
    const openFromUrl = async () => {
      setComposerSaving(true);
      const detail = await getTemplate(company.id, templateIdFromUrl);
      setComposerSaving(false);
      history.replace(routes.emailMarketingEmails);
      if (cancelled || !detail) {
        return;
      }
      openComposerFromTemplate(detail);
      toast.success(
        intl.formatMessage(
          { id: "app.emailMarketing.templates.applied" },
          { name: detail.name }
        )
      );
    };
    openFromUrl();
    return () => {
      cancelled = true;
    };
  }, [company?.id, templateIdFromUrl, getTemplate, history, routes.emailMarketingEmails, intl]);

  const defaultFromEmail =
    listSetting?.fromEmail || config?.activeSender?.email || "";
  const defaultFromName =
    listSetting?.fromName || config?.activeSender?.name || defaultFromEmail;

  const audienceName =
    composerListOptions.find((list) => list.id === composerTargetListId)?.name ||
    listSetting?.listName ||
    "Wembii";
  const audienceCount =
    composerListOptions.find((list) => list.id === composerTargetListId)?.subscribers ??
    overview?.subscribers ??
    0;

  const requireComposerList = () => {
    if (composerListOptions.length === 0) {
      toast.info(intl.formatMessage({ id: "app.emailMarketing.editor.noLists" }));
      return false;
    }
    if (!composerTargetListId) {
      toast.info(intl.formatMessage({ id: "app.emailMarketing.editor.selectListRequired" }));
      return false;
    }
    return true;
  };

  const copyListTargets = useMemo(() => {
    const targets = marketingLists.map((list) => ({
      id: list.id,
      label: list.name,
    }));
    if (!targets.length && audienceName) {
      targets.push({ id: "main-list", label: audienceName });
    }
    listTags.forEach((tag) => {
      if (!tag.archived && !targets.some((item) => item.label === tag.name)) {
        targets.push({ id: tag.id, label: tag.name });
      }
    });
    return targets;
  }, [audienceName, listTags, marketingLists]);

  const showComingSoonToast = () => {
    toast.info(intl.formatMessage({ id: "app.emailMarketing.mock.comingSoon" }));
  };

  const openCampaign = (id: string, status?: CampaignMessage["status"]) => {
    setOpenActionId(null);
    if (status === "sent") {
      goToEmailMarketingCampaign(id);
      return;
    }
    openEditor(id);
  };

  const openComposer = (type: CreateType = "plainText") => {
    setCreateType(type);
    setComposerCampaignId(null);
    setComposerTargetListId(
      defaultListId || listSetting?.sendgridListId || composerTargetListId
    );
    setComposerSubject(
      intl.formatMessage({ id: "app.emailMarketing.editor.defaultSubject" })
    );
    if (type === "html" || type === "dragDrop" || type === "smartDesigner") {
      setComposerHtml(DEFAULT_EMAIL_HTML);
    } else {
      setComposerHtml(DEFAULT_RICH_HTML);
    }
    setCreateMenuOpen(false);
    setComposerOpen(true);
  };

  const openComposerFromTemplate = (detail: ApiTemplateDetail) => {
    const type = mapTemplateTypeToCreateType(detail.type);
    setCreateType(type);
    setComposerCampaignId(null);
    setComposerTargetListId(
      defaultListId || listSetting?.sendgridListId || composerTargetListId
    );
    setComposerSubject(detail.subject || detail.name);
    setComposerHtml(resolveComposerHtmlFromTemplate(type, detail.html));
    setCreateMenuOpen(false);
    setComposerOpen(true);
  };

  const applyTemplateById = async (templateId: string) => {
    if (!company?.id) {
      return;
    }
    setComposerSaving(true);
    const detail = await getTemplate(company.id, templateId);
    setComposerSaving(false);
    if (!detail) {
      return;
    }
    openComposerFromTemplate(detail);
    toast.success(
      intl.formatMessage(
        { id: "app.emailMarketing.templates.applied" },
        { name: detail.name }
      )
    );
  };

  const openEditor = async (campaignId: string) => {
    if (!company?.id) {
      return;
    }
    setOpenActionId(null);
    setComposerSaving(true);
    const detail = await getCampaign(company.id, campaignId);
    setComposerSaving(false);
    if (!detail) {
      return;
    }
    setComposerCampaignId(detail.id);
    setComposerSubject(detail.subject);
    setComposerTargetListId(
      detail.sendgridListId || defaultListId || listSetting?.sendgridListId || null
    );
    const content = detail.htmlContent || "";
    const isRich =
      detail.createType === "plainText" ||
      (!content.includes("<table") && !content.includes("grapesjs"));
    setComposerHtml(
      isRich ? content || DEFAULT_RICH_HTML : content || DEFAULT_EMAIL_HTML
    );
    setCreateType((detail.createType as CreateType) || "plainText");
    setComposerOpen(true);
  };

  useEffect(() => {
    if (!company?.id || !editCampaignIdFromUrl) {
      return;
    }
    let cancelled = false;
    const openFromUrl = async () => {
      history.replace(routes.emailMarketingEmails);
      if (cancelled) {
        return;
      }
      await openEditor(editCampaignIdFromUrl);
    };
    openFromUrl();
    return () => {
      cancelled = true;
    };
  }, [company?.id, editCampaignIdFromUrl, history, routes.emailMarketingEmails]);

  const resolveComposerHtmlForSave = () => {
    if (createType === "plainText" && !composerHtml.includes("<table")) {
      return wrapRichHtmlForEmail(composerHtml);
    }
    return composerHtml;
  };

  const closeComposer = () => {
    setComposerOpen(false);
    setComposerCampaignId(null);
  };

  const persistComposerCampaign = async () => {
    if (!company?.id) {
      return null;
    }
    if (!requireComposerList()) {
      return null;
    }
    const body = {
      subject:
        composerSubject.trim() ||
        intl.formatMessage({ id: "app.emailMarketing.editor.defaultSubject" }),
      html: resolveComposerHtmlForSave(),
      audience: audienceName,
      createType,
      sendgridListId: composerTargetListId || undefined,
    };
    if (composerCampaignId) {
      return await updateCampaignApi(company.id, composerCampaignId, body);
    }
    const created = await createCampaignApi(company.id, body);
    if (created) {
      setComposerCampaignId(created.id);
    }
    return created;
  };

  const handleSaveDraft = async () => {
    setComposerSaving(true);
    await persistComposerCampaign();
    setComposerSaving(false);
    setActiveTab("drafts");
    closeComposer();
  };

  const handleComposerTest = async () => {
    if (!company?.id) {
      return;
    }
    if (!defaultFromEmail) {
      toast.info(intl.formatMessage({ id: "app.emailMarketing.api.senderRequired" }));
      return;
    }
    if (!requireComposerList()) {
      return;
    }
    setComposerSaving(true);
    const campaign = await persistComposerCampaign();
    setComposerSaving(false);
    if (!campaign) {
      return;
    }
    setTestModalCampaign({
      id: campaign.id,
      html: resolveComposerHtmlForSave(),
    });
    setTestEmailInput(user?.email || "");
    setTestTagsInput("");
  };

  const handleComposerSendNow = async () => {
    if (!company?.id) {
      return;
    }
    if (!defaultFromEmail) {
      toast.info(intl.formatMessage({ id: "app.emailMarketing.api.senderRequired" }));
      return;
    }
    const confirmMsg = intl.formatMessage(
      { id: "app.emailMarketing.editor.sendConfirm" },
      { audience: audienceName, count: audienceCount }
    );
    if (!window.confirm(confirmMsg)) {
      return;
    }
    setComposerSaving(true);
    const campaign = await persistComposerCampaign();
    if (campaign) {
      await sendCampaignNow(company.id, campaign.id, {
        notifyEmail: user?.email,
        html: resolveComposerHtmlForSave(),
        fromEmail: defaultFromEmail,
        fromName: defaultFromName,
      });
      await loadCampaigns(company.id);
      setActiveTab("sent");
      closeComposer();
    }
    setComposerSaving(false);
  };

  const isCampaignDeletable = (status: CampaignMessage["status"]) =>
    status === "draft" || status === "scheduled";

  const handleDeleteCampaign = async (campaignId: string, status: CampaignMessage["status"]) => {
    if (!company?.id) {
      return;
    }
    const confirmKey =
      status === "scheduled"
        ? "app.emailMarketing.emails.actions.deleteScheduledConfirm"
        : "app.emailMarketing.emails.actions.deleteConfirm";
    if (!window.confirm(intl.formatMessage({ id: confirmKey }))) {
      return;
    }
    setOpenActionId(null);
    setSelectedIds((current) => current.filter((id) => id !== campaignId));
    await deleteCampaignApi(company.id, campaignId);
    await loadOverview(company.id);
    await loadCampaigns(company.id);
  };

  const handleCancelSchedule = async (campaignId: string) => {
    if (!company?.id) {
      return;
    }
    setOpenActionId(null);
    await cancelScheduleCampaign(company.id, campaignId);
    await loadCampaigns(company.id);
  };

  const openTestModal = (message: CampaignMessage) => {
    setOpenActionId(null);
    setCopyListHoverId(null);
    setTestModalCampaign({ id: message.id, html: message.htmlContent });
    setTestEmailInput(user?.email || "");
    setTestTagsInput("");
  };

  const handleConfirmTestSend = async () => {
    if (!company?.id || !testModalCampaign) {
      return;
    }
    const email = testEmailInput.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(
        intl.formatMessage({ id: "app.emailMarketing.emails.testModal.invalidEmail" })
      );
      return;
    }
    if (!defaultFromEmail) {
      toast.info(intl.formatMessage({ id: "app.emailMarketing.api.senderRequired" }));
      return;
    }
    setTestSending(true);
    let html = testModalCampaign.html || "";
    if (!html) {
      const detail = await getCampaign(company.id, testModalCampaign.id);
      html = detail?.htmlContent || "";
    }
    await sendTest(
      company.id,
      testModalCampaign.id,
      email,
      applyTestPersonalization(html, testTagsInput),
      {
        fromEmail: defaultFromEmail,
        fromName: defaultFromName,
      }
    );
    setTestSending(false);
    setTestModalCampaign(null);
  };

  const openScheduleModal = (message: CampaignMessage) => {
    setOpenActionId(null);
    setCopyListHoverId(null);
    setScheduleModalCampaign({ id: message.id, subject: message.subject });
    setScheduleMode("later");
    setScheduleDateTime(defaultScheduleDateTime());
  };

  const handleConfirmSchedule = async () => {
    if (!company?.id || !scheduleModalCampaign) {
      return;
    }
    if (!defaultFromEmail) {
      toast.info(intl.formatMessage({ id: "app.emailMarketing.api.senderRequired" }));
      return;
    }
    if (scheduleMode === "now") {
      const confirmMsg = intl.formatMessage(
        { id: "app.emailMarketing.editor.sendConfirm" },
        { audience: audienceName, count: audienceCount }
      );
      if (!window.confirm(confirmMsg)) {
        return;
      }
    } else {
      const picked = new Date(scheduleDateTime);
      if (Number.isNaN(picked.getTime()) || picked.getTime() <= Date.now()) {
        toast.error(
          intl.formatMessage({ id: "app.emailMarketing.emails.scheduleModal.invalidDate" })
        );
        return;
      }
    }
    setScheduleSubmitting(true);
    const detail = await getCampaign(company.id, scheduleModalCampaign.id);
    if (!detail) {
      setScheduleSubmitting(false);
      return;
    }
    if (scheduleMode === "now") {
      await sendCampaignNow(company.id, scheduleModalCampaign.id, {
        notifyEmail: user?.email,
        html: detail.htmlContent,
        fromEmail: defaultFromEmail,
        fromName: defaultFromName,
      });
      setActiveTab("sent");
    } else {
      await scheduleCampaign(
        company.id,
        scheduleModalCampaign.id,
        new Date(scheduleDateTime).toISOString()
      );
      setActiveTab("scheduled");
    }
    await loadCampaigns(company.id);
    await loadOverview(company.id);
    setScheduleSubmitting(false);
    setScheduleModalCampaign(null);
  };

  const duplicateCampaignFromMessage = async (
    message: CampaignMessage,
    audienceOverride?: string,
    listIdOverride?: string
  ) => {
    if (!company?.id) {
      return;
    }
    setOpenActionId(null);
    setCopyListHoverId(null);
    setActionBusy(true);
    const detail = await getCampaign(company.id, message.id);
    if (!detail) {
      setActionBusy(false);
      return;
    }
    const copySuffix = intl.formatMessage({
      id: "app.emailMarketing.emails.actions.copySuffix",
    });
    const isMarketingList =
      listIdOverride && marketingLists.some((list) => list.id === listIdOverride);
    try {
      await emailMarketingApiService.createCampaign(company.id, {
        subject: audienceOverride
          ? detail.subject
          : `${detail.subject} ${copySuffix}`.trim(),
        audience: audienceOverride || detail.audience || audienceName,
        createType: detail.createType,
        html: detail.htmlContent,
        sendgridListId: isMarketingList
          ? listIdOverride
          : detail.sendgridListId || undefined,
      });
      await loadCampaigns(company.id);
      if (audienceOverride) {
        toast.success(
          intl.formatMessage(
            { id: "app.emailMarketing.emails.actions.copiedToList" },
            { list: audienceOverride }
          )
        );
      } else {
        toast.success(
          intl.formatMessage({ id: "app.emailMarketing.emails.actions.duplicated" })
        );
      }
      await loadOverview(company.id);
      setActiveTab("drafts");
    } catch (error) {
      console.error(error);
      toast.error(intl.formatMessage({ id: "app.toast.error13" }));
    }
    setActionBusy(false);
  };

  const emailTabs = useMemo(
    () => [
      { id: "all" as const, label: "app.emailMarketing.emails.tabs.all" },
      { id: "drafts" as const, label: "app.emailMarketing.emails.tabs.drafts" },
      {
        id: "scheduled" as const,
        label: "app.emailMarketing.emails.tabs.scheduled",
      },
      { id: "sent" as const, label: "app.emailMarketing.emails.tabs.sent" },
    ],
    []
  );

  const allCampaignsFromApi = apiCampaigns as CampaignMessage[];

  const allDrafts = useMemo(
    () => allCampaignsFromApi.filter((c) => c.status === "draft"),
    [allCampaignsFromApi]
  );

  const scheduledFromApi = useMemo(
    () => allCampaignsFromApi.filter((c) => c.status === "scheduled"),
    [allCampaignsFromApi]
  );

  const sentFromApi = useMemo(
    () => allCampaignsFromApi.filter((c) => c.status === "sent"),
    [allCampaignsFromApi]
  );

  const visibleMessages = useMemo(() => {
    let rows: CampaignMessage[];
    switch (activeTab) {
      case "drafts":
        rows = allDrafts;
        break;
      case "scheduled":
        rows = scheduledFromApi;
        break;
      case "sent":
        rows = sentFromApi;
        break;
      default:
        rows = allCampaignsFromApi;
    }
    const q = searchQuery.trim().toLowerCase();
    const filtered = q
      ? rows.filter(
          (row) =>
            row.subject.toLowerCase().includes(q) ||
            row.audience.toLowerCase().includes(q)
        )
      : rows;

    return [...filtered].sort((a, b) => {
      const aTs = getCampaignSortTimestamp(a);
      const bTs = getCampaignSortTimestamp(b);
      return dateSortDirection === "desc" ? bTs - aTs : aTs - bTs;
    });
  }, [
    activeTab,
    allDrafts,
    allCampaignsFromApi,
    scheduledFromApi,
    sentFromApi,
    searchQuery,
    dateSortDirection,
  ]);

  const deletableVisibleMessages = useMemo(
    () => visibleMessages.filter((message) => isCampaignDeletable(message.status)),
    [visibleMessages]
  );

  const allDeletableVisibleSelected =
    deletableVisibleMessages.length > 0 &&
    deletableVisibleMessages.every((message) => selectedIds.includes(message.id));

  const someSelected = selectedIds.length > 0;

  const toggleSelectAllVisible = () => {
    if (allDeletableVisibleSelected) {
      setSelectedIds((current) =>
        current.filter(
          (id) => !deletableVisibleMessages.some((message) => message.id === id)
        )
      );
      return;
    }
    const visibleIds = deletableVisibleMessages.map((message) => message.id);
    setSelectedIds((current) => {
      const merged = current.slice();
      visibleIds.forEach((id) => {
        if (!merged.includes(id)) {
          merged.push(id);
        }
      });
      return merged;
    });
  };

  const toggleSelectRow = (campaignId: string) => {
    setSelectedIds((current) =>
      current.includes(campaignId)
        ? current.filter((id) => id !== campaignId)
        : [...current, campaignId]
    );
  };

  const handleBulkDelete = async () => {
    if (!company?.id || selectedIds.length === 0) {
      return;
    }
    const toDelete = selectedIds.filter((id) => {
      const message = allCampaignsFromApi.find((item) => item.id === id);
      return message && isCampaignDeletable(message.status);
    });
    if (toDelete.length === 0) {
      return;
    }
    if (
      !window.confirm(
        intl.formatMessage(
          { id: "app.emailMarketing.emails.actions.bulkDeleteConfirm" },
          { count: toDelete.length }
        )
      )
    ) {
      return;
    }
    setBulkDeleting(true);
    const ok = await deleteCampaignsBulk(company.id, toDelete);
    setBulkDeleting(false);
    if (ok) {
      setSelectedIds([]);
      await loadOverview(company.id);
      await loadCampaigns(company.id);
    }
  };

  const showDateFilter = activeTab === "sent" || activeTab === "all";

  return (
    <Layout>
      <Content className="email-mkt email-mkt--emails">
        <section
          className={`email-mkt__hero email-mkt__hero--messages${
            createMenuOpen ? " is-create-menu-open" : ""
          }`}
        >
          <div className="email-mkt__hero-top">
            <div className="email-mkt__hero-copy">
              <span className="email-mkt__eyebrow">
                <FormattedMessage id="app.sidebar.emailMarketing" />
              </span>
              <h1 className="email-mkt__title">
                <FormattedMessage id="app.emailMarketing.emails.title" />
              </h1>
              <p className="email-mkt__subtitle">
                <FormattedMessage id="app.emailMarketing.emails.subtitle" />
              </p>
            </div>

            <div className="email-mkt__stats">
              <div className="email-mkt__stat">
                <strong>{overview?.sentCount ?? sentFromApi.length}</strong>
                <span>
                  <FormattedMessage id="app.emailMarketing.emails.stats.sent" />
                </span>
              </div>
              <div className="email-mkt__stat">
                <strong>{overview?.averageOpenRate ?? "26%"}</strong>
                <span>
                  <FormattedMessage id="app.emailMarketing.emails.stats.openRate" />
                </span>
              </div>
              <div className="email-mkt__stat">
                <strong>{overview?.draftCount ?? allDrafts.length}</strong>
                <span>
                  <FormattedMessage id="app.emailMarketing.emails.stats.drafts" />
                </span>
              </div>
            </div>
          </div>

          <div className="email-mkt__hero-actions" ref={createMenuRef}>
            <div className="email-mkt__create-wrap" ref={createWrapRef}>
              <div className="email-mkt__create-btn-group">
                <button
                  type="button"
                  className="email-mkt__create-btn"
                  onClick={() => openComposer("plainText")}
                >
                  <FormattedMessage id="app.emailMarketing.emails.createMessage" />
                </button>
                <button
                  type="button"
                  className="email-mkt__create-caret-btn"
                  aria-expanded={createMenuOpen}
                  aria-label={intl.formatMessage({
                    id: "app.emailMarketing.emails.createMessage",
                  })}
                  onClick={() => setCreateMenuOpen((open) => !open)}
                >
                  <span className="email-mkt__create-caret">
                    {createMenuOpen ? "^" : "v"}
                  </span>
                </button>
              </div>
            </div>
          </div>
          {createMenuOpen &&
            createMenuPosition &&
            createPortal(
              <div className="email-mkt__create-menu-portal">
                <div
                  id={CREATE_MENU_ID}
                  className="email-mkt__create-menu email-mkt__create-menu--portal"
                  style={{
                    top: createMenuPosition.top,
                    left: createMenuPosition.left,
                    width: createMenuPosition.width,
                  }}
                >
                {apiTemplates.length > 0 && (
                  <div className="email-mkt__create-menu-section">
                    <span className="email-mkt__create-menu-label">
                      {intl.formatMessage({
                        id: "app.emailMarketing.emails.create.fromTemplate",
                        defaultMessage: "Desde plantilla",
                      })}
                    </span>
                    {apiTemplates.slice(0, 8).map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => applyTemplateById(template.id)}
                      >
                        <strong>{template.name}</strong>
                        <span>
                          <FormattedMessage
                            id={
                              createTypeLabels[
                                mapTemplateTypeToCreateType(template.type)
                              ]
                            }
                          />
                        </span>
                      </button>
                    ))}
                    <button type="button" onClick={goToEmailMarketingTemplates}>
                      <strong>
                        <FormattedMessage id="app.emailMarketing.emails.browseTemplates" />
                      </strong>
                      <span>
                        <FormattedMessage id="app.emailMarketing.emails.browseTemplatesHelp" />
                      </span>
                    </button>
                  </div>
                )}
                <button type="button" onClick={() => openComposer("dragDrop")}>
                  <strong>
                    <FormattedMessage id="app.emailMarketing.emails.create.dragDrop" />
                  </strong>
                  <span>
                    <FormattedMessage id="app.emailMarketing.emails.create.dragDropHelp" />
                  </span>
                </button>
                <button type="button" onClick={() => openComposer("smartDesigner")}>
                  <strong>
                    <FormattedMessage id="app.emailMarketing.emails.create.smartDesigner" />
                  </strong>
                  <span>
                    <FormattedMessage id="app.emailMarketing.emails.create.smartDesignerHelp" />
                  </span>
                </button>
                <button type="button" onClick={() => openComposer("plainText")}>
                  <strong>
                    <FormattedMessage id="app.emailMarketing.emails.create.plainText" />
                  </strong>
                  <span>
                    <FormattedMessage id="app.emailMarketing.emails.create.plainTextHelp" />
                  </span>
                </button>
                <button type="button" onClick={() => openComposer("html")}>
                  <strong>
                    <FormattedMessage id="app.emailMarketing.emails.create.html" />
                  </strong>
                  <span>
                    <FormattedMessage id="app.emailMarketing.emails.create.htmlHelp" />
                  </span>
                </button>
              </div>
              </div>,
              document.body
            )}
        </section>

        <section
          className={`email-mkt__panel email-mkt__messages-panel${
            createMenuOpen ? " email-mkt__messages-panel--under-menu" : ""
          }`}
        >
          <div className="email-mkt__messages-toolbar">
            <div className="email-mkt__segmented email-mkt__segmented--messages">
              {emailTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={activeTab === tab.id ? "is-active" : ""}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setOpenActionId(null);
                    setSelectedIds([]);
                  }}
                >
                  <FormattedMessage id={tab.label} />
                </button>
              ))}
            </div>

            <div className="email-mkt__messages-filters">
              <input
                placeholder={intl.formatMessage({
                  id: "app.emailMarketing.emails.search",
                })}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {showDateFilter && (
                <button
                  type="button"
                  className="email-mkt__ghost-btn"
                  onClick={() => setDateFilterOpen((open) => !open)}
                >
                  <FormattedMessage id="app.emailMarketing.emails.noDates" />
                </button>
              )}
              <button
                type="button"
                className="email-mkt__ghost-btn"
                onClick={goToEmailMarketingTemplates}
              >
                <FormattedMessage id="app.emailMarketing.emails.newsletterHub" />
              </button>
            </div>
          </div>

          {dateFilterOpen && (
            <div className="email-mkt__date-filter">
              <label className="email-mkt__field">
                <span>
                  <FormattedMessage id="app.emailMarketing.emails.dateFrom" />
                </span>
                <input type="date" />
              </label>
              <label className="email-mkt__field">
                <span>
                  <FormattedMessage id="app.emailMarketing.emails.dateTo" />
                </span>
                <input type="date" />
              </label>
              <button
                type="button"
                className="email-mkt__primary-btn"
                onClick={() => {
                  setDateFilterOpen(false);
                  showComingSoonToast();
                }}
              >
                <FormattedMessage id="app.emailMarketing.emails.applyDates" />
              </button>
            </div>
          )}

          {someSelected && (
            <div className="email-mkt__bulk-bar">
              <span className="email-mkt__bulk-count">
                <FormattedMessage
                  id="app.emailMarketing.emails.bulk.selectedCount"
                  values={{ count: selectedIds.length }}
                />
              </span>
              <button
                type="button"
                className="email-mkt__ghost-btn is-danger"
                disabled={bulkDeleting}
                onClick={handleBulkDelete}
              >
                {bulkDeleting ? (
                  <FormattedMessage id="app.emailMarketing.emails.bulk.deleting" />
                ) : (
                  <FormattedMessage id="app.emailMarketing.emails.bulk.delete" />
                )}
              </button>
              <button
                type="button"
                className="email-mkt__ghost-btn"
                onClick={() => setSelectedIds([])}
              >
                <FormattedMessage id="app.emailMarketing.emails.bulk.clear" />
              </button>
            </div>
          )}

          {visibleMessages.length > 0 ? (
            <div className="email-mkt__messages-table">
              <div className="email-mkt__message-row email-mkt__message-row--head">
                <span className="email-mkt__row-check">
                  {deletableVisibleMessages.length > 0 ? (
                    <input
                      type="checkbox"
                      checked={allDeletableVisibleSelected}
                      ref={(input) => {
                        if (input) {
                          input.indeterminate =
                            someSelected && !allDeletableVisibleSelected;
                        }
                      }}
                      onChange={toggleSelectAllVisible}
                      aria-label={intl.formatMessage({
                        id: "app.emailMarketing.emails.bulk.selectVisible",
                      })}
                    />
                  ) : null}
                </span>
                <span>
                  <FormattedMessage id="app.emailMarketing.emails.subject" />
                </span>
                <span>
                  <FormattedMessage id="app.emailMarketing.emails.opened" />
                </span>
                <span>
                  <FormattedMessage id="app.emailMarketing.emails.clicked" />
                </span>
                <span>
                  <FormattedMessage id="app.emailMarketing.emails.unsubscribes" />
                </span>
                <span>
                  <FormattedMessage id="app.emailMarketing.emails.spam" />
                </span>
                <button
                  type="button"
                  className="email-mkt__col-sort is-active"
                  onClick={() =>
                    setDateSortDirection((current) =>
                      current === "desc" ? "asc" : "desc"
                    )
                  }
                  aria-sort={
                    dateSortDirection === "desc" ? "descending" : "ascending"
                  }
                  title={intl.formatMessage({
                    id:
                      dateSortDirection === "desc"
                        ? "app.emailMarketing.emails.sortDateNewest"
                        : "app.emailMarketing.emails.sortDateOldest",
                  })}
                >
                  <FormattedMessage id="app.emailMarketing.emails.date" />
                  <span className="email-mkt__col-sort-icon" aria-hidden="true">
                    {dateSortDirection === "desc" ? "↓" : "↑"}
                  </span>
                </button>
                <span>
                  <FormattedMessage id="app.emailMarketing.emails.status" />
                </span>
                <span />
              </div>
              {visibleMessages.map((message) => (
                <div
                  className={`email-mkt__message-row${
                    openActionId === message.id
                      ? " email-mkt__message-row--menu-open"
                      : ""
                  }`}
                  key={message.id}
                >
                  <span className="email-mkt__row-check">
                    {isCampaignDeletable(message.status) ? (
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(message.id)}
                        onChange={() => toggleSelectRow(message.id)}
                        onClick={(event) => event.stopPropagation()}
                        aria-label={intl.formatMessage({
                          id: "app.emailMarketing.emails.bulk.selectOne",
                        })}
                      />
                    ) : null}
                  </span>
                  <div className="email-mkt__message-subject">
                    <EmailPreviewThumb
                      html={message.htmlContent}
                      accent={message.accent}
                      subject={message.subject}
                    />
                    <div className="email-mkt__message-subject-text">
                      <button
                        type="button"
                        className="email-mkt__link-btn email-mkt__subject-btn"
                        onClick={() => openCampaign(message.id, message.status)}
                      >
                        {message.subject}
                      </button>
                      <small>
                        <CampaignAudienceLine
                          message={message}
                          intl={intl}
                          fallbackCount={audienceCount}
                        />
                      </small>
                    </div>
                  </div>
                  <CampaignMetric
                    isSent={message.status === "sent"}
                    value={message.opened}
                    count={message.openedCount}
                    intl={intl}
                  />
                  <CampaignMetric
                    isSent={message.status === "sent"}
                    value={message.clicked}
                    count={message.clickedCount}
                    intl={intl}
                  />
                  <CampaignMetric
                    isSent={message.status === "sent"}
                    value={message.unsubscribed || "—"}
                    count={message.unsubscribedCount || 0}
                    intl={intl}
                  />
                  <CampaignMetric
                    isSent={message.status === "sent"}
                    value={message.complaints}
                    count={message.complaintsCount}
                    intl={intl}
                  />
                  <span className="email-mkt__message-date">
                    {formatCampaignListDate(intl, message)}
                  </span>
                  <CampaignStatusPill status={message.status} />
                  <div className="email-mkt__row-actions">
                    <button
                      type="button"
                      className="email-mkt__row-menu-btn"
                      onClick={() =>
                        setOpenActionId((current) => (current === message.id ? null : message.id))
                      }
                      aria-label={intl.formatMessage({
                        id: "app.emailMarketing.emails.actions.more",
                      })}
                    >
                      ⋮
                    </button>
                    {openActionId === message.id && (
                      <div className="email-mkt__row-menu">
                        {message.status !== "sent" ? (
                          <button type="button" onClick={() => openEditor(message.id)}>
                            <FormattedMessage id="app.emailMarketing.emails.actions.review" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openCampaign(message.id, "sent")}
                          >
                            <FormattedMessage id="app.emailMarketing.emails.actions.stats" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => openTestModal(message)}
                          disabled={actionBusy}
                        >
                          <FormattedMessage id="app.emailMarketing.emails.actions.testSend" />
                        </button>
                        {message.status !== "sent" && (
                          <button
                            type="button"
                            onClick={() => openScheduleModal(message)}
                            disabled={actionBusy}
                          >
                            <FormattedMessage id="app.emailMarketing.emails.actions.schedule" />
                          </button>
                        )}
                        {message.status === "scheduled" && (
                          <button
                            type="button"
                            onClick={() => handleCancelSchedule(message.id)}
                          >
                            <FormattedMessage
                              id="app.emailMarketing.emails.actions.cancelSchedule"
                            />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => duplicateCampaignFromMessage(message)}
                          disabled={actionBusy}
                        >
                          <FormattedMessage id="app.emailMarketing.emails.actions.copy" />
                        </button>
                        <div
                          className="email-mkt__row-menu-submenu-wrap"
                          onMouseEnter={() => setCopyListHoverId(message.id)}
                          onMouseLeave={() => setCopyListHoverId(null)}
                        >
                          <button type="button" disabled={actionBusy}>
                            <FormattedMessage
                              id="app.emailMarketing.emails.actions.copyToList"
                            />
                            <span aria-hidden="true">&gt;</span>
                          </button>
                          {copyListHoverId === message.id && (
                            <div className="email-mkt__row-submenu">
                              {copyListTargets.map((target) => (
                                <button
                                  key={target.id}
                                  type="button"
                                  onClick={() =>
                                    duplicateCampaignFromMessage(
                                      message,
                                      target.label,
                                      marketingLists.some((list) => list.id === target.id)
                                        ? target.id
                                        : undefined
                                    )
                                  }
                                >
                                  {target.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {(message.status === "draft" ||
                          message.status === "scheduled") && (
                          <button
                            type="button"
                            className="is-danger"
                            onClick={() =>
                              handleDeleteCampaign(message.id, message.status)
                            }
                          >
                            <FormattedMessage
                              id="app.emailMarketing.emails.actions.delete"
                            />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="email-mkt__messages-empty">
              <div className="email-mkt__empty-icon">✉</div>
              <h2>
                <FormattedMessage
                  id={
                    activeTab === "sent"
                      ? "app.emailMarketing.emails.empty.sentTitle"
                      : activeTab === "scheduled"
                      ? "app.emailMarketing.emails.empty.scheduledTitle"
                      : "app.emailMarketing.emails.empty.draftsTitle"
                  }
                />
              </h2>
              <p>
                <FormattedMessage
                  id={
                    activeTab === "sent"
                      ? "app.emailMarketing.emails.empty.sentText"
                      : activeTab === "scheduled"
                      ? "app.emailMarketing.emails.empty.scheduledText"
                      : "app.emailMarketing.emails.empty.draftsText"
                  }
                />
              </p>
              <div className="email-mkt__empty-actions">
                <button
                  type="button"
                  className="email-mkt__primary-btn"
                  onClick={() => openComposer("dragDrop")}
                >
                  <FormattedMessage id="app.emailMarketing.emails.createMessage" />
                </button>
                {activeTab === "scheduled" && (
                  <button
                    type="button"
                    className="email-mkt__ghost-btn"
                    onClick={() => setActiveTab("drafts")}
                  >
                    <FormattedMessage id="app.emailMarketing.emails.goToDrafts" />
                  </button>
                )}
              </div>
              <button
                type="button"
                className="email-mkt__help-link"
                onClick={goToEmailMarketingTemplates}
              >
                <FormattedMessage
                  id={
                    activeTab === "scheduled"
                      ? "app.emailMarketing.emails.empty.scheduleHelp"
                      : "app.emailMarketing.emails.empty.draftHelp"
                  }
                />
              </button>
            </div>
          )}
        </section>

        <EmailMktModal
          open={Boolean(testModalCampaign)}
          elevated={composerOpen}
          title={<FormattedMessage id="app.emailMarketing.emails.testModal.title" />}
          onClose={() => {
            if (!testSending) {
              setTestModalCampaign(null);
            }
          }}
          footer={
            <>
              <button
                type="button"
                className="email-mkt__ghost-btn"
                onClick={() => setTestModalCampaign(null)}
                disabled={testSending}
              >
                <FormattedMessage id="app.emailMarketing.emails.wizard.close" />
              </button>
              <button
                type="button"
                className="email-mkt__primary-btn"
                onClick={handleConfirmTestSend}
                disabled={testSending}
              >
                <FormattedMessage id="app.emailMarketing.emails.testModal.submit" />
              </button>
            </>
          }
        >
          <p className="email-mkt__copy">
            <FormattedMessage id="app.emailMarketing.emails.testModal.help" />
          </p>
          <label className="email-mkt__field">
            <span>
              <FormattedMessage id="app.emailMarketing.emails.testModal.emailLabel" />
            </span>
            <input
              type="email"
              value={testEmailInput}
              onChange={(event) => setTestEmailInput(event.target.value)}
              placeholder={intl.formatMessage({
                id: "app.emailMarketing.emails.testModal.emailPlaceholder",
              })}
            />
          </label>
          <label className="email-mkt__field">
            <span>
              <FormattedMessage id="app.emailMarketing.emails.testModal.tagsLabel" />
            </span>
            <textarea
              rows={4}
              value={testTagsInput}
              onChange={(event) => setTestTagsInput(event.target.value)}
              placeholder={intl.formatMessage({
                id: "app.emailMarketing.emails.testModal.tagsPlaceholder",
              })}
            />
          </label>
          <p className="email-mkt__copy email-mkt__copy--muted">
            <FormattedMessage id="app.emailMarketing.emails.testModal.tagsHelp" />
          </p>
        </EmailMktModal>

        <EmailMktModal
          open={Boolean(scheduleModalCampaign)}
          title={<FormattedMessage id="app.emailMarketing.emails.scheduleModal.title" />}
          onClose={() => {
            if (!scheduleSubmitting) {
              setScheduleModalCampaign(null);
            }
          }}
          footer={
            <>
              <button
                type="button"
                className="email-mkt__ghost-btn"
                onClick={() => setScheduleModalCampaign(null)}
                disabled={scheduleSubmitting}
              >
                <FormattedMessage id="app.emailMarketing.emails.wizard.close" />
              </button>
              <button
                type="button"
                className="email-mkt__primary-btn"
                onClick={handleConfirmSchedule}
                disabled={scheduleSubmitting}
              >
                <FormattedMessage
                  id={
                    scheduleMode === "now"
                      ? "app.emailMarketing.emails.wizard.sendNow"
                      : "app.emailMarketing.emails.scheduleModal.submit"
                  }
                />
              </button>
            </>
          }
        >
          <p className="email-mkt__copy">
            <FormattedMessage
              id="app.emailMarketing.emails.scheduleModal.help"
              values={{ subject: scheduleModalCampaign?.subject || "" }}
            />
          </p>
          <div className="email-mkt__schedule-options">
            <label className="email-mkt__schedule-option">
              <input
                type="radio"
                name="schedule-mode"
                checked={scheduleMode === "now"}
                onChange={() => setScheduleMode("now")}
              />
              <span>
                <FormattedMessage id="app.emailMarketing.emails.wizard.sendNow" />
              </span>
            </label>
            <label className="email-mkt__schedule-option">
              <input
                type="radio"
                name="schedule-mode"
                checked={scheduleMode === "later"}
                onChange={() => setScheduleMode("later")}
              />
              <span>
                <FormattedMessage id="app.emailMarketing.emails.wizard.scheduleLater" />
              </span>
            </label>
          </div>
          {scheduleMode === "later" && (
            <label className="email-mkt__field">
              <span>
                <FormattedMessage id="app.emailMarketing.emails.wizard.scheduleDate" />
              </span>
              <input
                type="datetime-local"
                value={scheduleDateTime}
                onChange={(event) => setScheduleDateTime(event.target.value)}
              />
            </label>
          )}
        </EmailMktModal>

        <EmailComposer
          open={composerOpen}
          subject={composerSubject}
          html={composerHtml}
          initialMode={
            createType === "html"
              ? "html"
              : createType === "dragDrop" || createType === "smartDesigner"
              ? "visual"
              : "rich"
          }
          fromEmail={defaultFromEmail}
          fromName={defaultFromName}
          audienceName={audienceName}
          audienceCount={audienceCount}
          marketingLists={composerListOptions}
          targetListId={composerTargetListId}
          onTargetListIdChange={setComposerTargetListId}
          saving={composerSaving}
          onSubjectChange={setComposerSubject}
          onHtmlChange={setComposerHtml}
          onClose={closeComposer}
          onSaveDraft={handleSaveDraft}
          onSendTest={handleComposerTest}
          onSendNow={handleComposerSendNow}
        />
      </Content>
    </Layout>
  );
};

export const EmailMarketingReportsScreen = () => {
  const intl = useIntl();
  const { company } = useMeCore();
  const { goToEmailMarketingCampaign } = useRouterCore();
  const {
    reports: apiReports,
    suppressions: apiSuppressions,
    subscribers: apiSubscribers,
    campaigns: apiCampaigns,
    loadReports,
    loadSuppressions,
    loadCampaigns,
    loadSubscribers,
  } = useEmailMarketingCore();
  const [reportView, setReportView] = useState<ReportView>("general");
  const [exportJobsState, setExportJobsState] = useState<
    { name: string; status: "processing" | "ready"; date: string }[]
  >([]);

  useEffect(() => {
    if (!company?.id) {
      return;
    }
    loadReports(company.id);
    loadSuppressions(company.id);
    loadCampaigns(company.id);
    loadSubscribers(company.id);
  }, [company?.id, loadReports, loadSuppressions, loadCampaigns, loadSubscribers]);

  const reportCampaigns =
    apiReports?.campaigns?.length
      ? apiReports.campaigns
      : apiCampaigns.filter((c) => c.status === "sent");

  const openSeriesData = apiReports?.openSeries?.length ? apiReports.openSeries : [];
  const openRowsData = apiReports?.openRows?.length ? apiReports.openRows : [];
  const deliverabilityRows = apiSuppressions;

  const showMockToast = (id: string) => {
    toast.info(intl.formatMessage({ id }));
  };

  const addExportJob = () => {
    setExportJobsState((current) => [
      {
        name: intl.formatMessage({ id: "app.emailMarketing.reports.newExportDefault" }),
        status: "processing" as const,
        date: new Date().toLocaleDateString(),
      },
      ...current,
    ]);
    toast.info(intl.formatMessage({ id: "app.emailMarketing.reports.exportSoon" }));
  };

  return (
    <Layout>
      <Content className="email-mkt email-mkt--reports">
        <section className="email-mkt__reports-shell">
          <aside className="email-mkt__reports-nav">
            <h1>
              <FormattedMessage id="app.emailMarketing.reports.title" />
            </h1>
            <div className="email-mkt__reports-nav-group">
              {reportNavSections.map((section) => (
                <button
                  key={section.id}
                  type="button"
                  className={reportView === section.id ? "is-active" : ""}
                  onClick={() => setReportView(section.id)}
                >
                  <FormattedMessage id={section.label} />
                </button>
              ))}
            </div>
          </aside>

          <div className="email-mkt__reports-main">
            {reportView === "general" && (
              <>
                <section className="email-mkt__reports-heading">
                  <div>
                    <span className="email-mkt__eyebrow">
                      <FormattedMessage id="app.sidebar.emailMarketing" />
                    </span>
                    <h2>
                      <FormattedMessage id="app.emailMarketing.reports.opensTitle" />
                    </h2>
                    <p>
                      <FormattedMessage id="app.emailMarketing.reports.opensHelp" />
                    </p>
                  </div>
                  <button
                    type="button"
                    className="email-mkt__ghost-btn"
                    onClick={() => showMockToast("app.emailMarketing.mock.exportStarted")}
                  >
                    <FormattedMessage id="app.emailMarketing.reports.exportCsv" />
                  </button>
                </section>
                <div className="email-mkt__reports-filters">
                  <button type="button" className="email-mkt__ghost-btn">
                    <FormattedMessage id="app.emailMarketing.reports.dateRange" />
                  </button>
                  <button type="button" className="email-mkt__ghost-btn">
                    <FormattedMessage id="app.emailMarketing.reports.openTypes" />
                  </button>
                </div>
                <section className="email-mkt__time-card">
                  <div className="email-mkt__time-legend">
                    <span>
                      <i />
                      <FormattedMessage id="app.emailMarketing.reports.broadcasts" />
                    </span>
                    <span>
                      <i className="email-mkt__legend-workflow" />
                      <FormattedMessage id="app.emailMarketing.reports.workflows" />
                    </span>
                  </div>
                  <div className="email-mkt__line-chart">
                    <svg viewBox="0 0 1000 300" preserveAspectRatio="none" role="img">
                      <polyline
                        points={openSeriesData
                          .map((value, index) => {
                            const x = (index / (openSeriesData.length - 1)) * 1000;
                            const y = 290 - (value / 80) * 260;
                            return `${x},${y}`;
                          })
                          .join(" ")}
                      />
                    </svg>
                    <div className="email-mkt__chart-y-axis">
                      {[80, 70, 60, 50, 40, 30, 20, 10, 0].map((value) => (
                        <span key={value}>{value}</span>
                      ))}
                    </div>
                  </div>
                </section>
                <section className="email-mkt__opens-table">
                  <div className="email-mkt__opens-row email-mkt__opens-row--head">
                    <span>
                      <FormattedMessage id="app.emailMarketing.reports.time" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.reports.broadcasts" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.reports.workflows" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.reports.unique" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.reports.totalOpens" />
                    </span>
                  </div>
                  {openRowsData.map((row) => (
                    <div className="email-mkt__opens-row" key={row.time}>
                      <strong>{row.time}</strong>
                      <span>{row.broadcasts}</span>
                      <span>{row.workflows}</span>
                      <span>{row.unique}</span>
                      <span>{row.total}</span>
                    </div>
                  ))}
                </section>
              </>
            )}

            {reportView === "campaigns" && (
              <>
                <section className="email-mkt__reports-heading">
                  <div>
                    <span className="email-mkt__eyebrow">
                      <FormattedMessage id="app.sidebar.emailMarketing" />
                    </span>
                    <h2>
                      <FormattedMessage id="app.emailMarketing.reports.campaignsTitle" />
                    </h2>
                    <p>
                      <FormattedMessage id="app.emailMarketing.reports.campaignsHelp" />
                    </p>
                  </div>
                </section>
                <div className="email-mkt__messages-table email-mkt__messages-table--reports">
                  <div className="email-mkt__message-row email-mkt__message-row--head">
                    <span>
                      <FormattedMessage id="app.emailMarketing.emails.subject" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.reports.openRate" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.reports.clickRate" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.emails.date" />
                    </span>
                  </div>
                  {reportCampaigns.map((message) => (
                    <div
                      className="email-mkt__message-row email-mkt__message-row--clickable"
                      key={message.id}
                      onClick={() => goToEmailMarketingCampaign(message.id)}
                      role="button"
                      tabIndex={0}
                    >
                      <strong>{message.subject}</strong>
                      <span>{message.opened}</span>
                      <span>{message.clicked}</span>
                      <span>{message.date}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {reportView === "deliverability" && (
              <>
                <section className="email-mkt__reports-heading">
                  <div>
                    <span className="email-mkt__eyebrow">
                      <FormattedMessage id="app.sidebar.emailMarketing" />
                    </span>
                    <h2>
                      <FormattedMessage id="app.emailMarketing.reports.deliverabilityTitle" />
                    </h2>
                    <p>
                      <FormattedMessage id="app.emailMarketing.reports.deliverabilityHelp" />
                    </p>
                  </div>
                </section>
                <div className="email-mkt__deliverability-grid">
                  <div className="email-mkt__deliverability-card">
                    <strong>3.2%</strong>
                    <span>
                      <FormattedMessage id="app.emailMarketing.reports.bounces" />
                    </span>
                  </div>
                  <div className="email-mkt__deliverability-card">
                    <strong>0.00%</strong>
                    <span>
                      <FormattedMessage id="app.emailMarketing.reports.complaints" />
                    </span>
                  </div>
                  <div className="email-mkt__deliverability-card">
                    <strong>1</strong>
                    <span>
                      <FormattedMessage id="app.emailMarketing.reports.blocks" />
                    </span>
                  </div>
                  <div className="email-mkt__deliverability-card">
                    <strong>4</strong>
                    <span>
                      <FormattedMessage id="app.emailMarketing.reports.metrics.unsubscribes" />
                    </span>
                  </div>
                </div>
                <div className="email-mkt__table">
                  <div className="email-mkt__table-row email-mkt__table-row--head">
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.email" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.lists.suppressionReason" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.emails.date" />
                    </span>
                  </div>
                  {deliverabilityRows.map((entry) => (
                    <div className="email-mkt__table-row" key={entry.email}>
                      <strong>{entry.email}</strong>
                      <span>{entry.reason}</span>
                      <span>{entry.date}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {reportView === "activity" && (
              <>
                <section className="email-mkt__reports-heading">
                  <div>
                    <span className="email-mkt__eyebrow">
                      <FormattedMessage id="app.sidebar.emailMarketing" />
                    </span>
                    <h2>
                      <FormattedMessage id="app.emailMarketing.reports.activityTitle" />
                    </h2>
                    <p>
                      <FormattedMessage id="app.emailMarketing.reports.activityHelp" />
                    </p>
                  </div>
                </section>
                <div className="email-mkt__activity-timeline">
                  {apiSubscribers.length === 0 ? (
                    <p className="email-mkt__note">
                      <FormattedMessage id="app.emailMarketing.reports.activityEmpty" />
                    </p>
                  ) : (
                    apiSubscribers.map((subscriber) => (
                      <div className="email-mkt__activity-item" key={subscriber.email}>
                        <span className="email-mkt__activity-dot" />
                        <div>
                          <strong>{subscriber.email}</strong>
                          <p>
                            <FormattedMessage
                              id="app.emailMarketing.reports.activityOpened"
                              values={{ date: subscriber.updatedAt }}
                            />
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}

            {reportView === "exports" && (
              <>
                <section className="email-mkt__reports-heading">
                  <div>
                    <span className="email-mkt__eyebrow">
                      <FormattedMessage id="app.sidebar.emailMarketing" />
                    </span>
                    <h2>
                      <FormattedMessage id="app.emailMarketing.reports.exportsTitle" />
                    </h2>
                    <p>
                      <FormattedMessage id="app.emailMarketing.reports.exportsHelp" />
                    </p>
                  </div>
                  <button type="button" className="email-mkt__primary-btn" onClick={addExportJob}>
                    <FormattedMessage id="app.emailMarketing.reports.newExport" />
                  </button>
                </section>
                <div className="email-mkt__table email-mkt__table--actions">
                  <div className="email-mkt__table-row email-mkt__table-row--head">
                    <span>
                      <FormattedMessage id="app.emailMarketing.reports.exportName" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.emails.status" />
                    </span>
                    <span>
                      <FormattedMessage id="app.emailMarketing.emails.date" />
                    </span>
                    <span />
                  </div>
                  {exportJobsState.map((job) => (
                    <div className="email-mkt__table-row" key={`${job.name}-${job.date}`}>
                      <strong>{job.name}</strong>
                      <span className="email-mkt__pill">
                        <FormattedMessage
                          id={
                            job.status === "ready"
                              ? "app.emailMarketing.reports.exportReady"
                              : "app.emailMarketing.reports.exportProcessing"
                          }
                        />
                      </span>
                      <span>{job.date}</span>
                      <button
                        type="button"
                        className="email-mkt__ghost-btn"
                        onClick={() => showMockToast("app.emailMarketing.mock.downloadReady")}
                      >
                        <FormattedMessage id="app.emailMarketing.reports.download" />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      </Content>
    </Layout>
  );
};
